import { useEffect, useMemo, useRef, useState } from "react";
import { initialsOf } from "@/components/common/UserInitials";
import { useLocation } from "react-router-dom";
import { useNavigate } from "@/hooks/useDeskNavigate";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Search, FileText, Trash2, Pencil, User, UserCircle2, PauseCircle,
  MapPin, MapPinOff, Archive, Play, AlertTriangle,
  CalendarRange, CalendarDays, ChevronDown, LayoutGrid, Loader, Eye, LockIcon as LockIcon, Globe2,
  Paperclip, Palette, Tags, Info, Printer, FileDown,
  Clock, Layers, X, Flame,
} from "lucide-react";
import { toast } from "@/lib/toast";
import api, { dedupedGet, getWithRetry } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/context/PermissionsContext";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { BadgeCheck } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { useViewScope } from "@/context/ViewScopeContext";
import { usePersistedColFilters } from "@/lib/persistedFilters";
import DocumentStatusBadge, { DOC_STATUS_MAP } from "@/components/documents/DocumentStatusBadge";
import EmptyState from "@/components/ui/EmptyState";
import UserAvatar from "@/components/ui/UserAvatar";
import DocumentCreateDialog from "@/components/documents/DocumentCreateDialog";
import DocumentEditDialog from "@/components/documents/DocumentEditDialog";
import HeaderProfileGroup from "@/components/TopBar";
import HeaderPill from "@/components/ui/HeaderPill";
import ScopeSwitch from "@/components/ui/ScopeSwitch";
import KpiSelectedBadge from "@/components/ui/KpiSelectedBadge";
import { MODULE_COLORS } from "@/lib/moduleColors";
import { avatarColorForSeed } from "@/lib/avatarColors";
import ColumnHeaderMenu from "@/components/ui/ColumnHeaderMenu";
import AvatarNamePopover from "@/components/ui/AvatarNamePopover";
import AttachmentsThumbnailPopover from "@/components/documents/AttachmentsThumbnailPopover";
// Spec iter182 (2026-02-27) — Point 1a du backlog technique : la ligne
// de tableau `<tr>` (933 lignes de JSX) est désormais extraite dans son
// propre composant pour faire redescendre `Documents.jsx` sous les 3300
// lignes. Aucun changement visuel : passage explicite de toutes les
// closures/lookups/callbacks en props.
import DocumentTableRow from "@/components/documents/DocumentTableRow";
import { useUserDirectory } from "@/lib/userDirectory";
// Spec iter183 (2026-02-27) — Point 1b du backlog technique : barre de
// filtres extraite (169 lignes de JSX) vers son propre composant.
// Aucun changement visuel : les composants locaux (ActiveColumnFilterChips,
// QuickChip, PeriodePicker, LocationChipDropdown) et constantes (NONE,
// QUICK_G1, QUICK_G3, DOC_STATUS_MAP) restent définis ici et sont passés
// en props.
import DocumentFilters from "@/components/documents/DocumentFilters";
// Spec iter185 (2026-02-27) — Point 1c du backlog technique : bandeau
// haut + KPIs extraits vers son propre composant. DOCS_KPIS est
// ré-exporté depuis DocumentHeader pour rester utilisable ici (résolution
// des libellés lors du filtrage par colonne).
import DocumentsHeader, { DOCS_KPIS } from "@/components/documents/DocumentHeader";
import SearchClearButton from "@/components/ui/SearchClearButton";
import SearchDebounceBar from "@/components/ui/SearchDebounceBar";
import SearchShortcutHint from "@/components/ui/SearchShortcutHint";
import useSearchShortcut from "@/hooks/useSearchShortcut";
import useLatestRef from "@/hooks/useLatestRef";
import useScrollRestore, { clearScrollMemory } from "@/hooks/useScrollRestore";
import DeskConfirm from "@/components/crm/DeskConfirm";

const NONE = "__none__";

/**
 * formatRelativeShort — Format court d'un timestamp ISO pour affichage
 * en colonne « Activité » (spec iter264 point 5 + iter265 point 1).
 * Ex : « à l'instant », « 3 min », « 2 h », « 3 j », « 1 an ».
 * PAS de préfixe « il y a ». Plafond au-delà de 365 j = « N an[s] »
 * (iter265) au lieu d'accumuler indéfiniment les jours.
 */
function formatRelativeShort(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diffS = Math.max(0, Math.round((now - d) / 1000));
  if (diffS < 60) return "à l'instant";
  const diffM = Math.round(diffS / 60);
  if (diffM < 60) return `${diffM} min`;
  const diffH = Math.round(diffM / 60);
  if (diffH < 24) return `${diffH} h`;
  const diffD = Math.round(diffH / 24);
  if (diffD < 365) return `${diffD} j`;
  const diffY = Math.floor(diffD / 365);
  return `${diffY} an${diffY > 1 ? "s" : ""}`;
}

/**
 * docActivityAt — calcul de la date d'activité la plus récente pour un
 * document (spec rev55). Retourne la max de {updated_at, created_at,
 * steps[].closed_at, steps[].started_at} — même règle que le tri.
 */
function docActivityAt(d) {
  // Spec iter217 (2026-02-28) — Ajout de `last_modified_at` dans le
  // panier de candidats. Sans lui, les mutations qui stampent MODIFS
  // (iter210 : suspend/reopen/corriger/close_step/upload_step_file/
  // delete_step_file/step_removed_manual…) n'influençaient PAS le tri
  // « Activité récente ». Résultat : après suppression d'une étape,
  // DC0210/26 restait à sa position d'origine dans la liste triée par
  // dernière activité — bug reporté par l'utilisateur.
  // Spec iter289 (2026-09-03) — `updated_at` EXCLU : champ technique aussi
  // touché par des process système (propagation de type, migrations,
  // crons) → fausses « activités récentes » (bug DC0215/26, DC0217/26).
  // Seuls comptent : création, MODIFS utilisateur, ouverture/clôture d'étape.
  if (d.activity_at) return d.activity_at;  // iter290 — calcul serveur (source de vérité)
  const candidates = [d.created_at, d.last_activity_at];
  for (const s of (d.steps || [])) {
    if (s.closed_at) candidates.push(s.closed_at);
    if (s.started_at) candidates.push(s.started_at);
  }
  return candidates.filter(Boolean).sort().slice(-1)[0] || "";
}

// Status → colour map used ONLY by the vertical filet next to every row's
// reference badge and by the .statusPill 15% opacity background. The row
// itself must remain strictly #212121 — no full-width tint (spec, Feb 2026).
const STATUS_COLOR = {
  en_cours:      "#F97316",
  termine:       "var(--status-cloture-docs)",
  cloture:       "var(--status-cloture-docs)",
  suspendu:      "#0078D4",
  delai_depasse: "#EF4444",
  annule:        "#9CA3AF",
  ouvert:        "#D99B1E",
  a_corriger:    "#F9A826",
};

// Palette pour badge Type (spec 2026-07-17 septies — modernisation Documents).
// Deterministic color from the type id — same type always the same badge.
const TYPE_BADGE_PALETTE = [
  { bg: "rgba(0,120,212,0.14)",  fg: "#5BA3E3" },
  { bg: "rgba(139,92,246,0.14)", fg: "#B69AF5" },
  { bg: "rgba(216,90,48,0.14)",  fg: "#F4906F" },
  { bg: "rgba(59,109,17,0.16)",  fg: "#8AC46A" },
  { bg: "rgba(212,83,126,0.14)", fg: "#F0A0B8" },
  { bg: "rgba(249,188,38,0.14)", fg: "#F9D26E" },
  { bg: "rgba(93,202,165,0.14)", fg: "#87DBBA" },
];
const STATUS_LABEL = {
  ouvert:   "Ouvert",
  a_corriger: "À corriger",
  en_cours: "En cours",
  termine:  "À vérifier",
  cloture:  "Clôturé",
  suspendu: "Suspendu",
  annule:   "Annulé",
};


/**
 * DocumentDetailsPreview — Popover d'aperçu affiché au hover sur l'icône
 * « Détails » dans la liste des documents. Spec 2026-07-24 feature #5.
 * Refonte 2026-08-02 point #4 : format 3 cartes empilées (Infos / Étapes /
 * Champs) sur fond sombre, chaque carte a son propre header uppercase +
 * compteur/badge à droite. Standard réutilisable pour les futurs popovers.
 */
function DocumentDetailsPreview({ doc, usersById, projects, type }) {
  const projectName = doc.project_id
    ? (projects || []).find((p) => p.id === doc.project_id)?.name || "—"
    : "Non rattaché";
  const creator = doc.created_by_name
    || (usersById?.[doc.created_by]
        ? `${usersById[doc.created_by].first_name || ""} ${usersById[doc.created_by].last_name || ""}`.trim()
        : "—");
  const steps = doc.steps || [];
  // Spec iter218 (2026-02-28) — Les étapes soft-deleted comptent dans
  // le total (Y du X/Y) mais PAS dans les validées (X). L'indicateur
  // de progression les rend en gris (tombstone) pour rester visible.
  const closedCount = steps.filter((s) => s.closed_at && !s.deleted_at).length;
  const totalSteps = steps.length;
  const progressPct = totalSteps > 0 ? Math.round((closedCount / totalSteps) * 100) : 0;
  const values = doc.values || {};
  // Champs du type + champs propres au document sur mesure (doc.extra_fields), dans l'ordre défini — comme la page de détail
  const typeFields = (type?.fields || []).map((f, i) => [f, i]).sort((a, b) => ((a[0].order ?? a[1]) - (b[0].order ?? b[1])) || (a[1] - b[1])).map(([f]) => f);
  const allFields = [...typeFields, ...(doc.extra_fields || [])];
  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "2-digit" }) : "—";
  const renderFieldValue = (f) => {
    // Spec 2026-02-XX rev10 (Feb 2026) — les champs file supportent
    // désormais le multi-upload. Backward-compat lecture : `v` peut être
    // (a) un dict legacy (1 fichier), (b) une liste de dicts, (c) un
    // simple string (autre type).
    const v = values[f.key] ?? values[f.id];
    if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) return "—";
    if (typeof v === "object" && v?.original_filename) return `📎 ${v.original_filename}`;
    if (Array.isArray(v)) {
      // Multi-file : premier nom + "(+N)" si plusieurs.
      const files = v.filter((x) => x && typeof x === "object" && x.original_filename);
      if (files.length > 0) {
        return files.length === 1
          ? `📎 ${files[0].original_filename}`
          : `📎 ${files[0].original_filename} (+${files.length - 1})`;
      }
      return v.join(", ");
    }
    return String(v).slice(0, 80);
  };
  const statusColor = STATUS_COLOR[doc.status] || "#94a3b8";
  const statusLabel = STATUS_LABEL[doc.status] || (doc.status || "—");

  // Styles réutilisables pour les cartes internes (design standard).
  const cardStyle = { background: "#1E1E1E", border: "1px solid #2A2A2A" };
  const cardHeaderCls = "text-[10px] uppercase tracking-widest text-muted-foreground";

  return (
    <div className="space-y-3" style={{ background: "#0F0F10", margin: -16, padding: 16, borderRadius: 8 }}>
      {/* ── EN-TÊTE ─────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-white truncate" data-testid="doc-preview-title">
            {doc.title || doc.reference}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            {doc.reference} · {type?.name || "—"}
          </div>
        </div>
        <span
          className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full whitespace-nowrap shrink-0"
          style={{ background: `${statusColor}22`, color: statusColor, border: `1px solid ${statusColor}55` }}
          data-testid="doc-preview-status"
        >
          {statusLabel}
        </span>
      </div>

      {/* ── CARTE INFOS GÉNÉRALES ───────────────────────────────────── */}
      <div className="rounded-lg p-3" style={cardStyle}>
        <div className={cardHeaderCls + " mb-2"}>Infos générales</div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
          <div><span className="text-muted-foreground">Créé par : </span><strong className="text-white">{creator}</strong></div>
          <div><span className="text-muted-foreground">Créé le : </span><strong className="text-white">{fmt(doc.created_at)}</strong></div>
          <div><span className="text-muted-foreground">Projet : </span><strong className="text-white">{projectName}</strong></div>
          <div><span className="text-muted-foreground">Mis à jour : </span><strong className="text-white">{fmt(doc.updated_at)}</strong></div>
        </div>
      </div>

      {/* ── CARTE ÉTAPES & INTERVENANTS ─────────────────────────────── */}
      {/* Spec rev53 (2026-08-02) — enrichissement de l'affichage des étapes
          pour aligner sur le popover Tâches : chaque étape affiche l'avatar
          + nom de l'exécuteur (`closed_by`) avec statut de validation
          individuel (« Vu · validé <date> » vs « En attente »). C'est
          l'équivalent documentaire des « Assignés + statut de lecture »
          des tâches, puisqu'en Documents, l'assigné effectif d'une étape
          est celui qui la valide. */}
      {totalSteps > 0 && (() => {
        // Spec rev72 (2026-08-02) — dérive un `activeIdx` défensif pour
        // éviter la régression rev66 sur documents legacy où
        // `doc.current_step_index` peut être obsolète (documents suspendus,
        // ré-ouverts, remaniés). L'index actif est le premier step
        // non-clôturé ; les steps d'index > activeIdx sont "à venir".
        const isSequential = (doc.workflow_mode || "sequential") === "sequential";
        // Spec iter218 (2026-02-28) — Les étapes soft-deleted (deleted_at)
        // sont ignorées pour déterminer la « prochaine étape » : sinon,
        // supprimer l'étape courante ferait pointer l'indicateur sur la
        // ligne grise "supprimée".
        const firstUnclosedIdx = steps.findIndex((s) => !s.closed_at && !s.deleted_at);
        const activeIdx = firstUnclosedIdx >= 0
          ? firstUnclosedIdx
          : (doc.current_step_index ?? steps.length);
        return (
        <div className="rounded-lg p-3" style={cardStyle}>
          <div className="flex items-center justify-between mb-2">
            <div className={cardHeaderCls}>Étapes &amp; intervenants</div>
            <div className="text-[11px] tabular-nums text-white font-medium" data-testid="doc-preview-steps-count">
              {closedCount}/{totalSteps} validées
            </div>
          </div>
          {/* Barre de progression */}
          <div
            className="w-full h-1 rounded-full overflow-hidden mb-2"
            style={{ background: "#2A2A2A" }}
            role="progressbar"
            aria-valuenow={progressPct}
          >
            <div
              className="h-full transition-all"
              style={{ width: `${progressPct}%`, background: "#22C55E" }}
              data-testid="doc-preview-progress"
            />
          </div>
          {/* Liste des étapes (avec exécuteur + statut de validation) */}
          <ul className="space-y-1.5 max-h-52 overflow-y-auto pr-1">
            {steps.map((s, idx) => {
              const isDeleted = !!s.deleted_at;
              const isClosed = !!s.closed_at && !isDeleted;
              const exec = isClosed ? usersById?.[s.closed_by] : null;
              const execName = exec
                ? `${exec.first_name || ""} ${exec.last_name || ""}`.trim()
                : (isClosed ? "—" : null);
              const fmtDT = s.closed_at
                ? new Date(s.closed_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })
                : null;
              // Spec rev72 (2026-08-02) — remplace la lecture directe de
              // `doc.current_step_index` (fragile sur legacy) par `activeIdx`
              // dérivé du premier step non-clos (calculé plus haut). Corrige
              // la régression rev66 sur DC0159/26 et docs similaires.
              const isFuture = !isClosed && !isDeleted && isSequential && idx > activeIdx;
              // Spec iter218 (2026-02-28) — Étape soft-deleted (tombstone).
              // Rendu grisé + libellé barré, tooltip explicite avec le
              // motif + auteur si dispo. Compte dans le total mais pas
              // dans "validées".
              const deletedBy = isDeleted && s.deleted_by ? usersById?.[s.deleted_by] : null;
              const deletedByName = deletedBy
                ? `${deletedBy.first_name || ""} ${deletedBy.last_name || ""}`.trim()
                : null;
              const deletedDT = isDeleted && s.deleted_at
                ? new Date(s.deleted_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })
                : null;
              return (
                <li
                  key={s.id}
                  className="flex items-center gap-2 text-[11px]"
                  data-testid={`doc-preview-step-${s.id}`}
                >
                  {/* Avatar intervenant (validé), tombstone (supprimée), ou placeholder gris */}
                  {isClosed && exec && !isDeleted ? (
                    <span className="inline-flex rounded-full shrink-0 cursor-help" title={execName || "—"}>
                      <UserAvatar user={exec} size={22} showPopover={false} />
                    </span>
                  ) : (
                  <span
                    className="inline-flex items-center justify-center rounded-full shrink-0 cursor-help"
                    style={{
                      width: 22, height: 22,
                      background: isDeleted ? "#3F3F46" : "rgba(107,114,128,0.10)",
                      color: isDeleted ? "#9CA3AF" : "#6B7280",
                      fontSize: 9, fontWeight: 700,
                      border: "1px solid " + (isDeleted ? "rgba(156,163,175,0.30)" : "rgba(107,114,128,0.25)"),
                    }}
                    title={
                      isDeleted
                        ? `Étape supprimée${deletedDT ? ` le ${deletedDT}` : ""}${deletedByName ? ` par ${deletedByName}` : ""}${s.deleted_reason ? ` — motif : ${s.deleted_reason}` : ""}`
                        : (isClosed ? (execName || "—") : (isFuture ? "Étape à venir" : "En attente d'exécuteur"))
                    }
                  >
                    {isDeleted ? "×" : "?"}
                  </span>
                  )}
                  <div className="flex-1 min-w-0">
                    <div
                      className={"truncate leading-tight " + (isClosed ? "text-white" : "text-muted-foreground")}
                      style={isDeleted ? { textDecoration: "line-through", color: "#9CA3AF" } : undefined}
                    >
                      {s.label}
                    </div>
                  </div>
                  {/* Pill statut */}
                  <span
                    className="px-1.5 py-0.5 rounded-full text-[9px] font-medium whitespace-nowrap shrink-0 tabular-nums"
                    style={
                      isDeleted
                        ? { background: "rgba(107,114,128,0.15)", color: "#9CA3AF", border: "1px solid rgba(156,163,175,0.30)" }
                        : isClosed
                        ? { background: "rgba(34,197,94,0.15)", color: "#22C55E", border: "1px solid rgba(34,197,94,0.35)" }
                        : isFuture
                          ? { background: "#262931", color: "#6B7280", border: "1px solid rgba(107,114,128,0.25)" }
                          : { background: "rgba(107,114,128,0.10)", color: "#9CA3AF", border: "1px solid rgba(107,114,128,0.25)" }
                    }
                  >
                    {isDeleted ? "Supprimée" : (isClosed ? fmtDT : (isFuture ? "À venir" : "Non vu"))}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
        );
      })()}

      {/* ── CARTE CHAMPS DU TYPE ────────────────────────────────────── */}
      {(
        <div className="rounded-lg p-3" style={cardStyle}>
          <div className={cardHeaderCls + " mb-2"} data-testid="doc-preview-fields-count">Champs du type ({allFields.length + 1})</div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] max-h-56 overflow-y-auto pr-1">
            <div className="truncate" data-testid="doc-preview-field-project">
              <span className="text-muted-foreground">Siège / Projet : </span>
              <strong className={doc.project_id ? "text-white" : "text-muted-foreground/60"}>{projectName}</strong>
            </div>
            {allFields.map((f) => {
              const display = renderFieldValue(f);
              const isEmpty = display === "—";
              return (
                <div key={f.id} className="truncate" data-testid={`doc-preview-field-${f.id}`}>
                  <span className="text-muted-foreground">{f.label} : </span>
                  <strong className={isEmpty ? "text-muted-foreground/60" : "text-white"}>{display}</strong>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}



/**
 * Retourne { fg, bg } pour le badge d'un type de document.
 *
 * Spec 2026-02-XX rev10 (Feb 2026) — les couleurs sont désormais persistées
 * SUR le document type (champ ``color``). Cette fonction lit ce champ via
 * ``typesById`` et calcule un fond dérivé (12% opacity) + une bordure (28%).
 * Fallback vers la palette hardcodée quand aucune couleur n'est stockée
 * (types legacy pré-fix qui apparaîtraient encore côté client avant le
 * heal lazy du backend).
 */
function typeBadgeColor(id, typesById) {
  const type = id && typesById?.[id];
  if (type?.color) {
    return { fg: type.color, bg: type.color + "1F", border: type.color + "47" };
  }
  if (!id) return TYPE_BADGE_PALETTE[0];
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) & 0x7fffffff;
  return TYPE_BADGE_PALETTE[h % TYPE_BADGE_PALETTE.length];
}

// -------------------------------------------------------------------
// Quick chips — spec 2026-02-XX rev25 (Point 11) :
//   G1 : Mes en cours + Mes documents
//   G2 : Période (special component) + Siège / Projet (special component)
//   G3 : « En retard » retiré (redondant avec la KPI Délai dépassé).
//        Remplacé par 2 nouveaux filtres :
//          • Échéance aujourd'hui — docs avec étape active à échéance J
//          • Sans pièce jointe — docs sans aucun fichier attaché
// -------------------------------------------------------------------
const QUICK_G1 = [
  { v: "my_in_progress", l: "Mes en cours",   Icon: Play,        color: "#F97316" },
  { v: "my_documents",   l: "Mes documents",  Icon: UserCircle2, color: "#0078D4" },
];
const QUICK_G3 = [
  { v: "due_today",    l: "Échéance aujourd'hui", Icon: CalendarDays, color: "#F9BC26" },
  { v: "no_attachment", l: "Sans pièce jointe",   Icon: Paperclip,    color: "#8B95A5" },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" });
};

// Détecte si la saisie de recherche ressemble à une référence document.
const REF_PATTERN = /^#?DC\d{3,}(\/\d{2})?$/i;

const DOCS_MODULE_COLOR = MODULE_COLORS.documents || "#2B579A";

// Spec 2026-07-17 septies — 7 cartes de largeur égale.

// ---------------------------------------------------------------------------
// Spec rev119 (2026-02) — Puces récapitulatives des filtres de colonne.
// Ajoutée en ligne juste à droite de la barre de recherche. Chaque filtre
// actif est représenté par une puce compacte permettant un retrait direct.
// Fond bleu discret #0A2540, texte #6FA0E0, coins arrondis (pill).
// ---------------------------------------------------------------------------


export default function Documents() {
  const navigate = useNavigate();
  const location = useLocation();

  // Spec rev109 (2026-02) — quand on arrive depuis "Types de documents"
  // via un lien "N docs" cliquable, la query string ``?type_id=<uuid>``
  // pré-applique le filtre colonne « Type » (colFilters.type = [id]).
  // Effet one-shot : on nettoie l'URL après consommation pour ne pas
  // re-appliquer à chaque re-render.
  const { user } = useAuth();
  const { can: canPerm, scopeFor } = usePermissions();
  const docScope = scopeFor("documents");
  const isAdminLevel = user?.role === "admin" || user?.is_first_admin;

  // Spec rev121 (2026-02) — persistance des filtres de colonnes par
  // utilisateur / par module (localStorage). Voir `lib/persistedFilters.js`.
  // Spec iter154bis (2026-02) — déclaration REMONTÉE ici (au-dessus de
  // ``load()`` et de son ``useEffect``) parce que ``load`` et la dépendance
  // ``[…, colFilters.type]`` du useEffect lisent ``colFilters`` dès le
  // premier render. Sans ceci → ReferenceError « Cannot access 'colFilters'
  // before initialization » (TDZ) → crash « Un problème inattendu ».
  const [colFilters, setColFilters] = usePersistedColFilters("documents", user?.id);

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  // Spec 2026-02-XX rev10 (Feb 2026) — infinite scroll : on empile les
  // pages 1..N au lieu d'écraser. ``page`` = prochaine page à charger,
  // ``hasMore`` = flag calculé depuis backend.total vs items.length,
  // ``loadingMore`` = affichage skeleton en bas pendant l'append.
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 50;
  const [types, setTypes] = useState([]);
  // Point 6 (rev25) — {type_id: [user_id]} des utilisateurs non-admin habilités à VOIR ce type (matrice user_type_permissions).
  const [typeViewersByType, setTypeViewersByType] = useState({});
  const [typeViewersLoaded, setTypeViewersLoaded] = useState(false);
  const [projects, setProjects] = useState([]);
  const { users, byId: directoryById, loaded: usersLoaded } = useUserDirectory();
  const [loading, setLoading] = useState(true);
  const _SORT_LS_KEY = "ledesk:docs:sort";
  const _initialSort = (() => {
    try {
      const raw = localStorage.getItem(_SORT_LS_KEY);
      if (!raw) return { key: "created_at", dir: "desc" };
      const parsed = JSON.parse(raw);
      return {
        key: typeof parsed.key === "string" ? parsed.key : "created_at",
        dir: parsed.dir === "asc" ? "asc" : "desc",
      };
    } catch { return { key: "created_at", dir: "desc" }; }
  })();
  const [sortKey, setSortKey] = useState(_initialSort.key);
  const [sortDir, setSortDir] = useState(_initialSort.dir);
  const [loadError, setLoadError] = useState(null);

  // Spec 2026-08-02 point #5 (rev2) — restauration des filtres au retour
  // depuis une fiche document. Version PRÉCÉDENTE (setState dans useEffect)
  // créait une race condition :
  //   1. Mount → useState = défauts
  //   2. useEffect restore → setState(snap) (async, queued)
  //   3. useEffect load (deps) → fetch avec closure défauts (empty)
  //   4. Re-render avec snap → useEffect load → fetch avec snap
  //   5. Selon timing réseau, la réponse "empty" (étape 3) pouvait
  //      arriver APRÈS la réponse "snap" (étape 4) et écraser l'UI.
  //
  // Nouveau mécanisme : lecture SYNCHRONE dans l'initializer useState.
  // Aucune race, aucune double-requête, filtres visibles dès la 1re render.
  const RESTORE_KEY = "docs:filters:v1";
  const RESTORE_TTL_MS = 5 * 60 * 1000;
  const readSnapshot = () => {
    try {
      const raw = sessionStorage.getItem(RESTORE_KEY);
      if (!raw) return null;
      const snap = JSON.parse(raw);
      // iter130 — plus de TTL : l'état de liste vit toute la session (F5 = défaut).
      return snap?.saved_at ? snap : null;
    } catch { return null; }
  };
  // On lit UNE seule fois au montage via useState-initializer. Ne SUPPRIME
  // PAS le snapshot à la lecture (initializer appelé 2× en dev avec
  // StrictMode ; la suppression casserait la 2e lecture). Le snapshot est
  // overwritten à chaque clic de ligne et expire naturellement après TTL.
  const [initialSnap] = useState(() => readSnapshot());
  // iter415 pt 1 — au retour d'une fiche, on recharge d'un coup les N pages déjà affichées (ex. 50 / 178 → 2 pages = 100 lignes).
  const restorePagesRef = useRef(Math.max(1, Number(initialSnap?.page) || 1));
  const pageRef = useLatestRef(page);
  // iter415 pt 1 — qui a allumé « Chargement… » : un chargement annulé par un autre ne doit jamais laisser le loader allumé.
  const loaderRef = useRef({ main: false, more: false });
  useScrollRestore(RESTORE_KEY, !loading && items.length > 0, "[data-testid=docs-list-scroll]");

  // Filters state — spec iter263 point 2 (2026-02) : TOUS les filtres
  // sont restaurés au retour depuis une fiche (recherche, catégorie,
  // projet, période, showClosed, statusFilter, scope). Revert partiel
  // de iter102b — l'utilisateur a explicitement demandé le comportement
  // « complet » : ne rien perdre au Retour.
  const [search, setSearch] = useState(initialSnap?.search ?? "");
  // Spec rev160 (2026-02) — debounce de la recherche pour éviter les
  // appels backend à chaque frappe. La valeur "debouncedSearch" est celle
  // utilisée pour les fetches ; `search` reste liée à l'input pour un
  // affichage réactif. 250 ms permet à l'utilisateur de finir de taper
  // sans lag perceptible.
  const [debouncedSearch, setDebouncedSearch] = useState(initialSnap?.search ?? ""); // iter155 P3 — évite le double fetch au retour (loader orphelin)
  // Perf (Sept 2026) — anti-rebond 300 ms ; champ vidé → bascule immédiate
  // (la liste de base est restaurée depuis le cache, sans requête serveur).
  useEffect(() => {
    if (!search.trim()) { setDebouncedSearch(""); return undefined; }
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);
  const [searching, setSearching] = useState(false);
  // Cache de la liste NON filtrée par recherche (mêmes autres filtres) → restauration instantanée à l'effacement.
  const baseListRef = useRef(null);
  const abortRef = useRef(null);
  // Spec rev85 (2026-08-02) — retour à la recherche simple : l'input filtre
  // directement le tableau, sans dropdown intermédiaire (rev80/81 retirés).
  // `searchFocused` sert uniquement à masquer l'indice clavier « / ».
  const [searchFocused, setSearchFocused] = useState(false);
  // Spec rev82 (2026-08-02) — raccourci "/" pour focus la barre de recherche.
  const searchInputRef = useRef(null);
  useSearchShortcut(() => searchInputRef.current);
  const [statusFilter, setStatusFilter] = useState(initialSnap?.statusFilter ?? "");
  // Spec rev75 (2026-08-02) — filtre par catégorie transversale de type.
  const [categoryFilter, setCategoryFilter] = useState(initialSnap?.categoryFilter ?? "");
  const [typeCategories, setTypeCategories] = useState([]);
  // Spec rev76 (2026-08-02) — vue "Groupée par catégorie" — regroupe
  // visuellement les documents avec un en-tête de section par catégorie.
  const [groupByCategory, setGroupByCategory] = useState(initialSnap?.groupByCategory ?? false);
  const [scope, setScope] = useState(initialSnap?.scope ?? "");
  const [projectFilter, setProjectFilter] = useState(initialSnap?.projectFilter ?? "");
  const [period, setPeriod] = useState(initialSnap?.period ?? null);
  const [projectStats, setProjectStats] = useState({ unassigned: 0, projects: [] });
  const { viewScope, setViewScope } = useViewScope();
  // Spec 2026-07-18 (refonte archivage doc) — case "Afficher les clôturés"
  // remplace l'ancienne section /archives.
  // Spec iter263 point 2 — restauré au Retour.
  const [showClosed, setShowClosed] = useState(initialSnap?.showClosed ?? (localStorage.getItem(`ledesk_show_closed:${user?.id || ""}`) === "1"));
  useEffect(() => { localStorage.setItem(`ledesk_show_closed:${user?.id || ""}`, showClosed ? "1" : "0"); }, [showClosed, user?.id]);

  // iter130 — lit TOUJOURS les valeurs courantes (ref) : l'ancienne closure pouvait être
  // périmée (rangée mémoïsée) et écrasait le snapshot avec des filtres vides.
  const snapRef = useLatestRef({ statusFilter, scope, search, categoryFilter, groupByCategory, projectFilter, period, showClosed, page });
  const saveFiltersSnapshot = () => {
    try { sessionStorage.setItem(RESTORE_KEY, JSON.stringify({ saved_at: Date.now(), ...snapRef.current })); } catch { /* quota — ignore */ }
  };

  // Spec iter263 point 2 — auto-save COMPLET de tous les filtres (revert
  // partiel de iter102b). Le retour depuis une fiche restaure l'état exact.
  useEffect(() => {
    try {
      sessionStorage.setItem(RESTORE_KEY, JSON.stringify({
        saved_at: Date.now(),
        statusFilter, scope,
        search, categoryFilter, groupByCategory,
        projectFilter, period, showClosed, page,
      }));
    } catch { /* quota — ignore */ }
  }, [statusFilter, scope, search, categoryFilter, groupByCategory, projectFilter, period, showClosed, page]);

  // Dialogs
  const [createOpen, setCreateOpen] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [deleteDlg, setDeleteDlg] = useState(null);

  const isSearchingRef = REF_PATTERN.test(debouncedSearch.trim());

  // iter394 pt 1 — références (types + projets) chargées ENSEMBLE, avec reprises ; la liste n'est affichée que lorsqu'elles sont là.
  const [refsReady, setRefsReady] = useState(false);
  const refsAbortRef = useRef(null);
  const loadTypes = async (signal) => { const { data } = await getWithRetry("/document-types", { signal }); setTypes(data || []); };
  const loadProjects = async (signal) => { const { data } = await getWithRetry("/projects", { signal }); setProjects(data || []); };
  const loadRefs = async () => {
    if (refsAbortRef.current) refsAbortRef.current.abort();
    const ctrl = new AbortController(); refsAbortRef.current = ctrl;
    try { await Promise.all([loadTypes(ctrl.signal), loadProjects(ctrl.signal)]); if (!ctrl.signal.aborted) setRefsReady(true); return true; }
    catch (err) { if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return false; setLoadError("Les références (types, projets) n'ont pas pu être chargées."); setLoading(false); return false; }
  };
  const loadTypeCategories = async () => {
    // Spec rev75 (2026-08-02) — catégories transversales pour filtrer.
    try {
      const { data } = await api.get("/document-type-categories");
      setTypeCategories(data || []);
    } catch { /**/ }
  };
  const loadTypeViewers = async () => {
    // Point 6 — matrice user_type_permissions inversée : {type_id: [user_id]}
    try {
      const { data } = await api.get("/user-type-permissions/by-type/viewers");
      setTypeViewersByType(data || {});
    } catch { setTypeViewersByType({}); }
    setTypeViewersLoaded(true);
  };
  const loadProjectStats = async () => {
    try {
      const { data } = await dedupedGet("/documents/stats/by-project");
      setProjectStats(data || { unassigned: 0, projects: [] });
    } catch { /**/ }
  };

  // Spec rev127 (2026-02-05) — token de « dernière requête » pour éviter
  // les races quand plusieurs `load()` sont déclenchés en cascade (ex :
  // toggle auto de `showClosed` par le filtre colonne « Clôturé »). Sans
  // ce garde, la réponse d'un appel obsolète peut écraser un appel plus
  // récent, ce qui laissait la liste à jour avec le mauvais `include_closed`.
  const loadSeqRef = useRef(0);
  const baseKey = JSON.stringify([statusFilter, scope, projectFilter, period, viewScope, showClosed, colFilters.type, sortKey === "recent_activity"]);

  // Spec iter191 (2026-02-27) — Point 1 : `silent` flag pour éviter
  // le flash « Chargement… » lors du refetch de focus/visibility. Quand
  // `silent=true`, on ne touche pas à `loading`/`loadingMore` — la liste
  // déjà affichée reste visible pendant le rafraîchissement en
  // arrière-plan (comportement Gmail-like).
  const load = async ({ append = false, targetPage = 1, silent = false } = {}) => {
    const s = debouncedSearch.trim().replace(/^#/, "");
    // Annule la requête précédente encore en vol (réponses périmées jamais appliquées).
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController(); abortRef.current = ctrl;
    const isSearch = !!s && !append;
    // iter415 pt 1 — rechargement silencieux : on garde les N pages déjà affichées ; au 1er chargement, N vient du snapshot (retour de fiche).
    let pages = 1;
    if (!append && targetPage === 1) pages = Math.max(1, restorePagesRef.current, silent ? pageRef.current : 1);
    if (!silent && !isSearch) {
      if (append) { setLoadingMore(true); loaderRef.current.more = true; } else { setLoading(true); loaderRef.current.main = true; }
    }
    if (isSearch) setSearching(true);
    const mySeq = ++loadSeqRef.current;
    if (!s && !append && refsReady && baseListRef.current && baseListRef.current.key === baseKey && !silent) {
      // Effacement de la recherche : liste complète restaurée immédiatement.
      const c = baseListRef.current;
      setItems(c.items); setTotalCount(c.total); setStats(c.stats); setHasMore(c.items.length < c.total); setPage(c.pages || 1); setLoading(false); loaderRef.current.main = false;
      silent = true;
    }
    try {
      const params = new URLSearchParams();
      if (s) params.set("search", s);
      if (statusFilter) params.set("status", statusFilter);
      // Point 11 (rev25) — client-only scopes non envoyés au backend
      if (scope && scope !== "due_today" && scope !== "no_attachment" && scope !== "to_revalidate") {
        params.set("scope", scope);
      }
      if (projectFilter) params.set("project_id", projectFilter);
      if (period?.from) params.set("created_from", period.from);
      if (period?.to) params.set("created_to", period.to);
      // Case "Afficher les clôturés" — envoyée seulement si activée pour ne
      // pas polluer les URL par défaut.
      if (showClosed) params.set("include_closed", "true");
      // Spec iter154 (Feb 2026) — le filtre colonne « Type » (potentiellement
      // multi-sélection) devient un vrai filtre SERVEUR (avant : purement
      // client, ce qui plafonnait la liste à la page 1 = 50 docs et rendait
      // le compteur du bouton « Imprimer » trompeur pour les gros types).
      if (Array.isArray(colFilters.type) && colFilters.type.length > 0) {
        params.set("type_id", colFilters.type.join(","));
      } else if (typeof colFilters.type === "string" && colFilters.type) {
        params.set("type_id", colFilters.type);
      }
      // view_scope : envoyé uniquement quand global (défaut = mine côté serveur)
      params.set("view_scope", viewScope.startsWith("user:") ? "mine" : viewScope);
      if (viewScope.startsWith("user:")) params.set("view_as", viewScope.slice(5));
      // Spec 2026-02-XX rev10 — pagination classique côté backend, empilage
      // côté client pour donner l'illusion d'un scroll infini.
      params.set("page", String(targetPage));
      params.set("per_page", String(PAGE_SIZE * pages));
      // iter128 — tri « Activité récente » désormais côté serveur (champ indexé activity_at) :
      // l'ordre est global (toutes pages) et non limité à la page chargée.
      if (sortKey === "recent_activity") params.set("sort", "recent_activity");
      setLoadError(null);
      // iter394 pt 1 — 2 reprises automatiques ; au-delà de 10 s au total → abandon + message « Réessayer » (jamais de chargement infini).
      const deadline = setTimeout(() => ctrl.abort("deadline"), 10000);
      let data;
      try { ({ data } = await getWithRetry(`/documents?${params.toString()}`, { signal: ctrl.signal, timeout: 10000 })); } finally { clearTimeout(deadline); }
      if (!s && !append && Array.isArray(data.items)) baseListRef.current = { key: baseKey, items: data.items, total: data.total || 0, stats: data.stats || {}, pages };
      // Anti-race : on ignore la réponse si un appel plus récent a été lancé
      // entre-temps (ex : StrictMode double-mount, ou setShowClosed(true) qui
      // enchaîne un nouveau fetch avant l'arrivée du précédent).
      if (mySeq !== loadSeqRef.current) return;
      const newItems = data.items || [];
      const total = data.total || 0;
      setTotalCount(total);
      setStats(data.stats || {});
      setItems((prev) => append ? [...prev, ...newItems] : newItems);
      const newCount = (append ? items.length : 0) + newItems.length;
      setHasMore(newCount < total);
      setPage(append ? targetPage : pages);
      if (!append) restorePagesRef.current = 1;
      if (!append) loadProjectStats();
    } catch (err) {
      const deadlineHit = ctrl.signal.aborted && ctrl.signal.reason === "deadline";
      if (mySeq !== loadSeqRef.current || (!deadlineHit && (err?.code === "ERR_CANCELED" || err?.name === "CanceledError"))) return;
      const timedOut = deadlineHit || err?.code === "ECONNABORTED" || /timeout/i.test(err?.message || "");
      baseListRef.current = null;
      setLoadError(timedOut ? "Le chargement a pris trop de temps." : (err.response?.data?.detail || "Le chargement a échoué."));
      if (!append) setItems([]);
    } finally {
      if (mySeq === loadSeqRef.current) {
        setSearching(false);
        // iter415 pt 1 — le DERNIER chargement éteint tout loader allumé, même par un chargement qu'il a annulé.
        if (loaderRef.current.main || isSearch) { setLoading(false); loaderRef.current.main = false; }
        if (loaderRef.current.more || isSearch) { setLoadingMore(false); loaderRef.current.more = false; }
      }
    }
  };

  // iter415 pt 1 — garde-fou : jamais de « Chargement… » infini. 12 s sans lignes ni erreur → abandon + « Réessayer ».
  useEffect(() => {
    if (loadError || (!loading && refsReady)) return undefined;
    const t = setTimeout(() => {
      abortRef.current?.abort("deadline"); refsAbortRef.current?.abort();
      loaderRef.current = { main: false, more: false }; setLoading(false); setLoadingMore(false);
      setLoadError("Le chargement a pris trop de temps.");
    }, 12000);
    return () => clearTimeout(t);
  }, [loading, refsReady, loadError]);

  // Charge la page suivante — utilisée par l'IntersectionObserver et le bouton
  // de secours "Charger plus" (fallback si l'observer n'est pas supporté).
  const loadMore = () => {
    if (loading || loadingMore || !hasMore) return;
    load({ append: true, targetPage: page + 1 });
  };

  useEffect(() => {
    loadRefs(); loadProjectStats(); loadTypeViewers(); loadTypeCategories();
    return () => { refsAbortRef.current?.abort(); abortRef.current?.abort(); }; // iter394 pt 1 — requêtes annulées en quittant le module
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Tout changement de filtre server-side → reset page 1 + fetch neuf.
    // NB : on branche sur `debouncedSearch` (et non `search`) pour éviter
    // qu'une frappe rapide déclenche N fetches successifs.
    // Spec iter154 — ``colFilters.type`` déclenche aussi un refetch parce
    // qu'il est désormais poussé au serveur (comptage exact multi-types).
    load({ append: false, targetPage: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, statusFilter, scope, projectFilter, period, viewScope, showClosed, JSON.stringify(colFilters.type), sortKey === "recent_activity"]);

  // Spec rev155 (2026-02-07) — rafraîchissement temps réel de la liste
  // (et donc du chip « Étapes à revalider · N ») dès qu'une correction
  // ou une revalidation se produit sur DocumentDetail. Trois canaux :
  //   • CustomEvent "ledesk:doc-mutated" (émis par DocumentDetail dans
  //     le même onglet — cas normal utilisateur : correction, puis
  //     retour Documents).
  //   • Cross-tab via localStorage (setItem "ledesk:doc-mutated") —
  //     un onglet Documents déjà ouvert se met à jour quand une
  //     correction survient dans un autre onglet DocumentDetail.
  //   • visibilitychange (safety net) — reprise d'onglet en cas
  //     d'événement raté.
  //
  // Spec rev164 (2026-02) — bugfix : le softReload doit lire la
  // *dernière* version de `load` (qui embarque `debouncedSearch` +
  // `statusFilter` + …). Sans ce ref, la useEffect capturait la
  // version initiale de load (avec `debouncedSearch=""`) et écrasait
  // silencieusement la liste filtrée à chaque retour de focus fenêtre.
  // Spec rev166 (2026-02) — refactor via le hook générique
  // `useLatestRef` (voir memory/project_rules_spec.md).
  const loadRef = useLatestRef(load);
  useEffect(() => {
    // Petit "debounce" pour éviter les rechargements en rafale quand
    // plusieurs événements arrivent d'un coup (ex : batch de fixes).
    let timer = null;
    const softReload = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        // Rechargement silencieux : reset page 1 mais préserve l'UI
        // (chip actif, filtres, position). `load` gère l'anti-race
        // via `loadSeqRef`. loadRef.current garantit qu'on appelle
        // la version courante (avec les filtres à jour).
        if (loadRef.current) loadRef.current({ append: false, targetPage: 1, silent: true });
      }, 250);
    };
    const onCustomEvent = (e) => {
      // On répond à tous les mutations doc — le chip est calculé à
      // partir de `items`, donc un refresh suffit à recalculer N.
      softReload();
    };
    const onStorage = (e) => {
      if (e.key === "ledesk:doc-mutated") softReload();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") softReload();
    };
    // Spec iter191 (2026-02-27) — Point 3 : polling léger 20s (silent
    // refetch). Ne s'exécute QUE si l'onglet est visible pour éviter
    // le trafic parasite et respecter la batterie sur mobile.
    const POLL_MS = 20000;
    const poll = setInterval(() => {
      // Pas de rafraîchissement pendant la frappe dans la recherche (évite d'annuler la requête en cours).
      if (document.visibilityState === "visible" && document.activeElement !== searchInputRef.current) softReload();
    }, POLL_MS);
    window.addEventListener("ledesk:doc-mutated", onCustomEvent);
    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      if (timer) clearTimeout(timer);
      clearInterval(poll);
      window.removeEventListener("ledesk:doc-mutated", onCustomEvent);
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sentinel pour IntersectionObserver — quand l'élément entre dans le
  // viewport (200px avant le bas), on déclenche loadMore(). Spec rev10.
  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "200px" },
    );
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loading, loadingMore, page]);

  const typesById = useMemo(() => {
    const m = {}; (types || []).forEach((t) => { m[t.id] = t; }); return m;
  }, [types]);
  const projectsById = useMemo(() => {
    const m = {}; (projects || []).forEach((p) => { m[p.id] = p; }); return m;
  }, [projects]);
  // Résolution : annuaire léger (cache app) + utilisateurs embarqués dans la réponse /documents (résolution immédiate).
  const usersById = useMemo(() => {
    const m = { ...directoryById };
    (items || []).forEach((d) => { if (d.created_by_user) m[d.created_by_user.id] = m[d.created_by_user.id] || d.created_by_user; if (d.last_modified_by_user) m[d.last_modified_by_user.id] = m[d.last_modified_by_user.id] || d.last_modified_by_user; });
    return m;
  }, [directoryById, items]);

  const activeKpi = scope === "suspended" ? "suspendu"
    : scope === "my_in_progress" ? "en_cours"
    : scope === "delai_depasse" ? "delai_depasse"
    : statusFilter;

  const onSelectKpi = (key) => {
    // Spec iter132 pt.2 (Feb 2026) — deselect systématique remet TOUT à
    // zéro (statusFilter, scope, ET showClosed). Sans ça, la carte
    // Clôturé apparaissait comme désélectionnée mais la case « Afficher
    // les clôturés » restait cochée en fond → double indication verte
    // au 2ᵉ clic + il fallait un 3ᵉ clic pour tout nettoyer.
    if (!key) { setStatusFilter(""); setScope(""); setShowClosed(false); return; }
    if (key === "suspendu") { setScope(scope === "suspended" ? "" : "suspended"); setStatusFilter(""); return; }
    if (key === "delai_depasse") {
      // Spec 2026-02-XX rev9 — Délai dépassé n'est PAS un status persisté ;
      // on route vers le scope backend homonyme (alias de ``late``) qui
      // filtre les docs avec au moins une étape overdue non-fermée.
      setScope(scope === "delai_depasse" ? "" : "delai_depasse");
      setStatusFilter("");
      return;
    }
    if (key === "cloture") {
      // Spec 2026-07-18 (refonte archivage doc) — la carte "Clôturé" filtre
      // directement dans la liste principale (les clôturés ne vivent plus
      // dans une section séparée). On force `showClosed=true` pour que la
      // requête backend les renvoie.
      // Spec iter132 pt.2 — toggle propre en 2 états : le 2ᵉ clic (via la
      // branche `if (!key)` ci-dessus) remet AUSSI showClosed à false.
      const willActivate = statusFilter !== "cloture";
      setStatusFilter(willActivate ? "cloture" : "");
      setScope("");
      setShowClosed(willActivate);
      return;
    }
    setStatusFilter(key); setScope("");
  };

  // ------------------------------------------------------------------
  // Spec 2026-02-XX (Feb 2026) — bouton "Imprimer les résultats (N)".
  // Génère une synthèse PDF via POST /api/documents/export/print-summary
  // en passant tous les IDs actuellement visibles (post-filtres). Le
  // libellé du filtre actif est reconstruit ici pour l'afficher en titre.
  //
  // Spec 2026-02-XX rev8 (Feb 2026 bugfix) — depuis l'ajout des filtres
  // par colonne (rev7 : Type / Statut / Siège / Créateur / Responsable +
  // tri), il faut envoyer ``sortedItems`` (post-filtres CLIENT) et non
  // ``items`` (post-filtres SERVEUR uniquement). Sans ce fix le PDF
  // imprimait TOUS les documents alors que le tableau était filtré.
  // ------------------------------------------------------------------
  const buildFilterLabel = () => {
    const parts = [];
    // KPI / scope label
    if (scope === "suspended") parts.push("Documents suspendus");
    else if (scope === "my_in_progress") parts.push("Mes documents en cours");
    else if (scope === "delai_depasse") parts.push("Documents avec délai dépassé");
    else if (scope === "to_revalidate") parts.push("Documents avec étapes à corriger");
    else if (statusFilter) {
      const kpi = DOCS_KPIS.find((k) => k.key === statusFilter);
      parts.push(kpi?.label ? `Documents · ${kpi.label}` : `Statut ${statusFilter}`);
    }
    if (projectFilter) {
      const p = projectsById[projectFilter];
      if (p) parts.push(`Projet: ${p.name}`);
    }
    if (period?.from || period?.to) {
      parts.push(`Créés ${period.from || "…"} → ${period.to || "…"}`);
    }
    const s = search.trim();
    if (s) parts.push(`Recherche: "${s}"`);
    // Filtres par colonne (rev7). Chacun rejoint le libellé pour que le
    // titre du PDF reflète FIDÈLEMENT ce que voit l'utilisateur.
    if (colFilters.status) {
      const kpi = DOCS_KPIS.find((k) => k.key === colFilters.status);
      parts.push(`Statut: ${kpi?.label || colFilters.status}`);
    }
    if (colFilters.type) {
      const arr2 = Array.isArray(colFilters.type) ? colFilters.type : [colFilters.type];
      if (arr2.length === 1) {
        const t = typesById[arr2[0]];
        if (t) parts.push(`Type: ${t.name}`);
      } else if (arr2.length > 1) {
        const names = arr2.map((tid) => {
          const t = typesById[tid];
          return t ? t.name : null;
        }).filter(Boolean);
        parts.push(`Types (${names.length}): ${names.join(", ")}`);
      }
    }
    if (colFilters.project) {
      const p = projectsById[colFilters.project];
      if (p) parts.push(`Siège/Projet: ${p.name}`);
    }
    if (colFilters.creator) {
      const u = usersById[colFilters.creator];
      if (u) parts.push(`Créé par: ${u.first_name} ${u.last_name}`.trim());
    }
    // Spec iter213 (2026-02-28) — chip pour le filtre « modifier » sur MODIFS.
    if (colFilters.modifier) {
      const u = usersById[colFilters.modifier];
      if (u) parts.push(`Modifié par: ${u.first_name} ${u.last_name}`.trim());
    }
    if (colFilters.resp) {
      const u = usersById[colFilters.resp];
      if (u) parts.push(`Resp.: ${u.first_name} ${u.last_name}`.trim());
    }
    if (!parts.length) return "Tous les documents visibles";
    return parts.join(" · ");
  };

  // Spec 2026-02-XX rev6 (Feb 2026) — nouveau workflow "Aperçu avant
  // téléchargement" pour Print Summary. L'utilisateur veut vérifier le
  // rendu du PDF avant de le sauvegarder, plutôt que de forcer un
  // download systématique. On stocke le blob localement, ouvre un modal
  // avec un ``<iframe>`` sur l'object URL (utilise le PDF viewer natif
  // du navigateur), et propose un bouton "Télécharger" séparé.
  const [pdfPreview, setPdfPreview] = useState(null); // { url, filename, filterLabel }

  const printSummary = async () => {
    // Spec 2026-02-XX rev10 — le bouton "Imprimer les résultats" charge
    // TOUS les documents matchant les filtres serveur en un seul call
    // (per_page=5000) plutôt que d'utiliser sortedItems qui ne contient
    // que la page chargée en cours. Les filtres colonnes CLIENT sont
    // ensuite appliqués sur le résultat complet.
    try {
      // 1) Fetch complet avec les mêmes filtres serveur que l'affichage.
      const params = new URLSearchParams();
      const s = search.trim().replace(/^#/, "");
      if (s) params.set("search", s);
      if (statusFilter) params.set("status", statusFilter);
      if (scope && scope !== "due_today" && scope !== "no_attachment" && scope !== "to_revalidate") {
        params.set("scope", scope);
      }
      if (projectFilter) params.set("project_id", projectFilter);
      if (period?.from) params.set("created_from", period.from);
      if (period?.to) params.set("created_to", period.to);
      if (showClosed) params.set("include_closed", "true");
      // Spec iter154 (Feb 2026) — filtre type multi-valeurs poussé au
      // serveur pour ne PAS être plafonné par per_page.
      if (Array.isArray(colFilters.type) && colFilters.type.length > 0) {
        params.set("type_id", colFilters.type.join(","));
      } else if (typeof colFilters.type === "string" && colFilters.type) {
        params.set("type_id", colFilters.type);
      }
      params.set("view_scope", viewScope.startsWith("user:") ? "mine" : viewScope);
      if (viewScope.startsWith("user:")) params.set("view_as", viewScope.slice(5));
      params.set("page", "1");
      // Spec iter154 — cap relevé côté serveur à 50 000 pour permettre
      // d'imprimer intégralement les gros datasets (ex. 10 000+ chèques).
      params.set("per_page", "50000");
      const { data } = await api.get(`/documents?${params.toString()}`);
      let allItems = data.items || [];
      // Spec rev130 (2026-02-05) — applique EXACTEMENT les mêmes filtres
      // client que ceux utilisés pour construire `sortedItems` afin
      // que le PDF exporté corresponde 1:1 à ce que l'utilisateur voit
      // à l'écran (et au compteur du bouton).
      if (categoryFilter) {
        const typesInCategory = new Set(
          (types || []).filter((t) => t.category_id === categoryFilter).map((t) => t.id),
        );
        allItems = allItems.filter((d) => typesInCategory.has(d.type_id));
      }
      if (colFilters.status)  allItems = allItems.filter((d) => d.status === colFilters.status);
      if (colFilters.type) {
        const arr2 = Array.isArray(colFilters.type) ? colFilters.type : [colFilters.type];
        if (arr2.length > 0) allItems = allItems.filter((d) => arr2.includes(d.type_id));
      }
      if (colFilters.project) allItems = allItems.filter((d) => d.project_id === colFilters.project);
      if (colFilters.creator) allItems = allItems.filter((d) => d.created_by === colFilters.creator);
      // Spec iter213 (2026-02-28) — filtre par dernier modificateur.
      if (colFilters.modifier) allItems = allItems.filter((d) => d.last_modified_by === colFilters.modifier);
      if (colFilters.resp)    allItems = allItems.filter((d) => currentResponsible(d) === colFilters.resp);
      if (colFilters.proEtat && Array.isArray(colFilters.proEtat) && colFilters.proEtat.length > 0) {
        const sel = new Set(colFilters.proEtat);
        allItems = allItems.filter((d) => {
          if (d.status === "ouvert") return sel.has("ouvert");
          if (d.status === "cloture") return sel.has("cloture");
          // Spec iter218 (2026-02-28) — étapes supprimées ignorées.
          const openSteps = (d.steps || []).filter((s) => !s.closed_at && !s.deleted_at);
          const liveSteps = (d.steps || []).filter((s) => !s.deleted_at);
          if (openSteps.length === 0 && liveSteps.length > 0) return sel.has("termine");
          const hasLate = openSteps.some((s) => s.is_late === true);
          return sel.has(hasLate ? "retard" : "en_cours");
        });
      }
      if (colFilters.progress && Array.isArray(colFilters.progress) && colFilters.progress.length > 0) {
        const sel = new Set(colFilters.progress);
        allItems = allItems.filter((d) => {
          const info = stepInfo(d);
          if (!info.total) return false;
          let seenOpen = false;
          let outOfOrder = false;
          for (const s of (d.steps || [])) {
            if (s.deleted_at) continue; // Spec iter218 — ignore tombstones
            const closed = !!s.closed_at;
            if (!closed) seenOpen = true;
            else if (seenOpen) { outOfOrder = true; break; }
          }
          if (outOfOrder && sel.has("out_of_order")) return true;
          // Spec iter218 (2026-02-28) — le dénominateur inclut les
          // tombstones, mais un doc est « terminé » quand toutes les
          // étapes VIVANTES sont closes. On compare donc à total - deleted.
          const deletedCount = (d.steps || []).filter((s) => s.deleted_at).length;
          const liveTotal = info.total - deletedCount;
          if (info.closed === 0) return sel.has("not_started");
          if (info.closed >= liveTotal) return sel.has("done");
          return sel.has("in_progress");
        });
      }
      if (colFilters.attachments && Array.isArray(colFilters.attachments) && colFilters.attachments.length > 0) {
        const sel = new Set(colFilters.attachments);
        allItems = allItems.filter((d) => {
          const n = attachmentsCount(d);
          if (n > 0) return sel.has("with");
          return sel.has("without");
        });
      }
      if (scope === "due_today") {
        const todayYmd = new Date().toISOString().slice(0, 10);
        allItems = allItems.filter((d) => {
          const steps = d.steps || [];
          return steps.some((s) => !s.closed_at && s.deadline && s.deadline.slice(0, 10) === todayYmd);
        });
      } else if (scope === "no_attachment") {
        allItems = allItems.filter((d) => attachmentsCount(d) === 0);
      } else if (scope === "to_revalidate") {
        // Spec rev154 — impression cohérente avec le filtre à l'écran.
        allItems = allItems.filter((d) =>
          (d.steps || []).some((s) => s.corrected_at && !s.closed_at)
        );
      }
      // Spec iter251 (2026-03-01) — TOUJOURS exclure les documents annulés
      // de l'export/impression, même si un filtre actif les inclut dans
      // le tableau. Un annulé peut rester visible à l'écran (transparence
      // historique) mais ne doit jamais partir en synthèse imprimée.
      allItems = allItems.filter((d) => d.status !== "annule" && !d.cancelled);
      if (!allItems.length) {
        toast.error("Aucun document à imprimer");
        return;
      }
      const resp = await api.post(
        "/documents/export/print-summary",
        {
          document_ids: allItems.map((it) => it.id),
          filter_label: buildFilterLabel(),
        },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(resp.data);
      const filename = `synthese-documents-${new Date().toISOString().slice(0, 10)}.pdf`;
      // Révoque un éventuel object URL précédent pour éviter la fuite mémoire.
      setPdfPreview((prev) => {
        if (prev?.url) URL.revokeObjectURL(prev.url);
        return { url, filename, filterLabel: buildFilterLabel() };
      });
    } catch (err) {
      toast.error("Impression impossible", { description: err.response?.data?.detail || "" });
    }
  };

  const downloadPdfPreview = () => {
    if (!pdfPreview?.url) return;
    const a = document.createElement("a");
    a.href = pdfPreview.url;
    a.download = pdfPreview.filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };
  // Spec 2026-02-XX rev7 (Feb 2026) — bouton "Imprimer directement".
  // Déclenche window.print() sur le contenu de l'iframe → envoie le PDF
  // à l'imprimante physique sans passer par la sauvegarde locale (utile
  // pour bureaux avec imprimante en libre-service).
  const printPdfDirectly = () => {
    const iframe = document.querySelector('[data-testid="pdf-preview-iframe"]');
    if (!iframe) return;
    try {
      // Focus l'iframe pour que window.print() cible bien son contenu
      // (sinon Chrome/Firefox impriment la page hôte).
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } catch (e) {
      // Fallback : certains navigateurs bloquent l'accès contentWindow
      // pour raisons de sécurité (blob URL cross-origin). Dans ce cas
      // on ouvre le PDF dans un onglet séparé et l'utilisateur imprime
      // depuis là.
      toast.error("Impression directe indisponible — utilisez « Télécharger » puis imprimez depuis votre lecteur PDF");
    }
  };
  const closePdfPreview = () => {
    setPdfPreview((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return null;
    });
  };

  const openEdit = async (docSummary) => {
    if (!canPerm("documents", "read")) return toast.error("Accès non autorisé — le droit « Lecture » est requis pour ouvrir une fiche");
    try {
      const { data } = await api.get(`/documents/${docSummary.id}`);
      setEditDoc(data);
    } catch (err) {
      toast.error("Impossible d'ouvrir le document", { description: err.response?.data?.detail || "" });
    }
  };

  // Étape courante (nom) + progression % (workflow métier)
  // Spec iter155 — déclaration ``function`` (hoistée) plutôt que
  // ``const arrow`` pour tolérer les usages en amont via closure
  // (ex. depuis ``printSummary``) tout en gardant la garde-fou TDZ
  // sur les vraies ``const`` en TDZ.
  function stepInfo(d) {
    const steps = d.steps || [];
    const total = steps.length;
    if (total === 0) {
      const t = typesById[d.type_id];
      const wf = t?.workflow_steps || [];
      return { currentLabel: wf[0]?.label || "—", closed: 0, total: wf.length, percent: 0 };
    }
    // Spec iter218 (2026-02-28) — Les étapes soft-deleted comptent dans
    // le total (Y du X/Y) mais PAS dans les validées (X). L'indicateur
    // visuel les rend en gris (tombstone) pour rester visible.
    const closed = steps.filter((s) => s.closed_at && !s.deleted_at).length;
    // La « courante » est la 1ʳᵉ étape ni close ni supprimée.
    const currentIdx = steps.findIndex((s) => !s.closed_at && !s.deleted_at);
    const currentLabel = currentIdx >= 0
      ? (steps[currentIdx]?.label || "—")
      : "—";
    return { currentLabel, closed, total, percent: total ? Math.round((closed / total) * 100) : 0 };
  }

  /**
   * Étape ACTIVE (non-clôturée) courante d'un document — utilisée par la
   * colonne « Échéance ». Retourne le premier step ouvert, ou null si le
   * document est entièrement clôturé.
   *
   * Spec 2026-02-XX rev12 (batch UX) — surface l'échéance de l'étape en
   * cours directement dans la table pour repérer les documents à risque.
   */
  const activeStep = (d) => {
    const steps = d.steps || [];
    return steps.find((s) => !s.closed_at && !s.deleted_at) || null;
  };

  // Spec rev86 (2026-08-02) — nouvelle colonne « Pro. étape ».
  // Retourne TOUTES les étapes ouvertes du document (utile en workflow
  // parallèle où plusieurs étapes peuvent être actives simultanément).
  // Spec iter218 (2026-02-28) — exclut les étapes soft-deleted.
  const activeStepsAll = (d) => (d.steps || []).filter((s) => !s.closed_at && !s.deleted_at);

  // Nombre de jours de retard (minimum 1 dès qu'is_late === true).
  const daysLate = (deadlineIso) => {
    if (!deadlineIso) return 0;
    try {
      const dl = new Date(deadlineIso).getTime();
      const now = Date.now();
      if (Number.isNaN(dl) || now <= dl) return 0;
      return Math.max(1, Math.floor((now - dl) / 86400000));
    } catch { return 0; }
  };

  // Choisit l'étape la plus urgente parmi une liste : retard prioritaire
  // (plus grand retard en premier), sinon date la plus proche.
  const pickUrgentStep = (steps) => {
    if (!steps || steps.length === 0) return null;
    const late = steps.filter((s) => s.is_late === true && s.deadline);
    if (late.length > 0) {
      return [...late].sort((a, b) => new Date(a.deadline) - new Date(b.deadline))[0];
    }
    const dated = steps.filter((s) => s.deadline);
    if (dated.length === 0) return null;
    return [...dated].sort((a, b) => new Date(a.deadline) - new Date(b.deadline))[0];
  };

  /**
   * Formatte une date ISO en `JJ MMM · HH:MM` (français).
   * Retourne "—" si null/undefined.
   */
  const fmtDateTimeShort = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      const dd = String(d.getDate()).padStart(2, "0");
      // Spec iter266 point 6 — abréviations cohérentes AVEC point ("janv.")
      // sur tous les formats de la page. Aligné sur les normes fr-FR.
      const mm = ["janv.","févr.","mars","avr.","mai","juin","juil.","août","sept.","oct.","nov.","déc."][d.getMonth()];
      const HH = String(d.getHours()).padStart(2, "0");
      const MM = String(d.getMinutes()).padStart(2, "0");
      return `${dd} ${mm} · ${HH}:${MM}`;
    } catch { return "—"; }
  };
  const fmtDateShort = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = ["janv.","févr.","mars","avr.","mai","juin","juil.","août","sept.","oct.","nov.","déc."][d.getMonth()];
      return `${dd} ${mm}`;
    } catch { return "—"; }
  };
  // Spec iter266 point 6 (2026-02) — l'ancien format long (« 15 juillet »)
  // pouvait déborder des colonnes étroites (Étape « Clôturé le »). On passe
  // au format abrégé cohérent avec `fmtDateShort` (« 15 juil. »).
  const fmtDateFull = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = ["janv.","févr.","mars","avr.","mai","juin","juil.","août","sept.","oct.","nov.","déc."][d.getMonth()];
      return `${dd} ${mm}`;
    } catch { return "—"; }
  };

  // ------------------------------------------------------------------
  // Spec 2026-02-XX rev7 (Feb 2026) — tri/filtrage par en-têtes de colonnes.
  // Comportement identique à TaskListView : chaque en-tête cliquable ouvre
  // un menu ▾ (composant ``<ColumnHeaderMenu>``) qui propose tri croissant/
  // décroissant, plus un filtre par valeur pour les colonnes dimensionnelles
  // (Type / Statut / Projet / Créé par / Responsable). Tri/filtre appliqués
  // côté client sur la liste déjà chargée pour ne pas surcharger le backend.
  // ------------------------------------------------------------------
  // Spec iter217 (2026-02-28) — Persistance de sortKey / sortDir dans
  // localStorage. Bug reporté par l'utilisateur : après avoir supprimé
  // une étape sur DC0210/26 et être revenu à la liste, le tri
  // « Activité récente » se resettait à « Créé le » et le doc modifié
  // se retrouvait à sa position d'origine — d'où l'impression que le
  // stamp MODIFS ne fonctionnait pas. Persistance : la préférence de
  // l'utilisateur survit désormais à la navigation.
  useEffect(() => {
    try {
      localStorage.setItem(_SORT_LS_KEY, JSON.stringify({ key: sortKey, dir: sortDir }));
    } catch { /* localStorage quota ignored */ }
  }, [sortKey, sortDir]);
  // Spec rev127 (2026-02-05) — auto-liaison filtre colonne « Pro. étape :
  // Clôturé » ↔ toggle « Afficher les clôturés ». Sans ceci, sélectionner
  // « Clôturé » dans le filtre de colonne renvoyait 0 résultat car les
  // docs `status="cloture"` sont exclus du GET /documents par défaut.
  // Effet : dès qu'on coche « Clôturé » dans le menu de la colonne,
  // `showClosed` bascule automatiquement à true → le backend renvoie
  // les docs clôturés → le filtre local peut les afficher.
  // On NE force PAS le décochage : si l'utilisateur retire « Clôturé »
  // du filtre colonne, il peut vouloir garder « Afficher les clôturés »
  // pour consulter tous les clôturés sans filtre supplémentaire.
  useEffect(() => {
    const proEtat = colFilters?.proEtat;
    if (Array.isArray(proEtat) && proEtat.includes("cloture") && !showClosed) {
      setShowClosed(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colFilters?.proEtat]);
  // Spec rev109 — consomme `?type_id=` de l'URL une seule fois.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("scope") === "delai_depasse") { setScope("delai_depasse"); setStatusFilter(""); navigate("/documents", { replace: true }); return; }
    if (params.get("scope") === "my_in_progress") { setScope("my_in_progress"); setStatusFilter(""); navigate("/documents", { replace: true }); return; }
    const tid = params.get("type_id");
    if (!tid) return;
    setColFilters((prev) => ({ ...prev, type: [tid] }));
    // Nettoie l'URL pour ne pas re-déclencher au retour arrière.
    navigate("/documents", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const doSort = (key, dir) => { setSortKey(key); setSortDir(dir); };
  const doFilter = (key, value) => setColFilters((prev) => ({ ...prev, [key]: value }));

  // Compteur pièces jointes — fix bug 2026-07-20 #4 : évite le triple
  // comptage. Une pièce est comptée UNE SEULE FOIS via la source
  // autoritative :
  //   • Champs dynamiques du document → dict avec ``original_filename``.
  //   • Étapes → ``field_filenames`` (map multi-fichier). Fallback sur le
  //     mirror legacy ``field_filename`` UNIQUEMENT si la map est vide.
  // Spec iter155 — ``function`` hoistée (cf. stepInfo ci-dessus).
  function attachmentsCount(d) {
    let n = 0;
    const vals = d.values || {};
    Object.values(vals).forEach((v) => {
      // Spec 2026-02-XX rev10 — les champs file peuvent contenir soit un
      // dict legacy soit une liste de dicts (multi-upload).
      if (Array.isArray(v)) {
        n += v.filter((f) => f && typeof f === "object" && f.original_filename).length;
      } else if (v && typeof v === "object" && v.original_filename) {
        n += 1;
      }
    });
    const seen = new Set();
    (d.steps || []).forEach((s) => {
      const ff = s.field_filenames || {};
      // Spec 2026-02-XX ext#5 — les fichiers d'étape supportent le
      // multi-upload : `field_filenames[field_id]` peut être scalaire
      // (legacy) ou array (multi).
      let ffCount = 0;
      Object.values(ff).forEach((v) => {
        if (Array.isArray(v)) v.filter(Boolean).forEach((x) => { ffCount += 1; seen.add(String(x)); });
        else if (v) { ffCount += 1; seen.add(String(v)); }
      });
      if (ffCount > 0) {
        n += ffCount;
      } else if (s.field_filename) {
        // Legacy step avec un seul fichier stocké sur le mirror.
        n += 1; seen.add(String(s.field_filename));
      }
    });
    // iter394 pt 2 — pièces du dossier (tableau racine `pieces[]`, ex. DC0410/26 sans étape), non supprimées, sans doublon avec les étapes.
    (d.pieces || []).forEach((p) => {
      if (!p || typeof p !== "object" || p.deleted_at) return;
      const key = String(p.file?.original_filename || p.filename || p.original_filename || p.file?.storage_path || p.id || "");
      if (key && seen.has(key)) return;
      seen.add(key); n += 1;
    });
    return n;
  }

  // Responsable étape = celui qui a clôturé la première étape non-clôturée ?
  // Fallback : dernier "closed_by" connu, sinon "—".
  // Spec iter155 — ``function`` hoistée (cf. stepInfo ci-dessus).
  function currentResponsible(d) {
    const steps = d.steps || [];
    // Le dernier step clôturé indique le dernier acteur ; si tout est ouvert,
    // c'est le créateur qui porte la charge.
    for (let i = steps.length - 1; i >= 0; i--) {
      if (steps[i]?.closed_by) return steps[i].closed_by;
    }
    return d.created_by;
  }
  const displayName = (uid) => {
    if (!uid) return "—";
    if (uid === user?.id) return "Moi";
    const u = usersById[uid];
    if (!u) return "—";
    return `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email || "—";
  };

  // Spec 2026-02-XX rev7 — tri/filtre par colonne appliqués côté client sur
  // la liste chargée. Idempotent (memo sur items + sort + colFilters) pour
  // éviter les recalculs inutiles.
  const sortedItems = useMemo(() => {
    let arr = items;
    // Spec rev75 (2026-08-02) — filtre par catégorie transversale : garde
    // les docs dont le TYPE référence la catégorie sélectionnée.
    if (categoryFilter) {
      const typesInCategory = new Set(
        (types || []).filter((t) => t.category_id === categoryFilter).map((t) => t.id),
      );
      arr = arr.filter((d) => typesInCategory.has(d.type_id));
    }
    if (colFilters.status)  arr = arr.filter((d) => (colFilters.status === "annule" ? !!d.cancelled || d.status === "annule" : d.status === colFilters.status));
    // Spec 2026-02-XX rev10 — la colonne Type supporte le MULTI-select ;
    // ``colFilters.type`` est un array (union de tous les types cochés).
    // Backward-compat : si legacy string, on la traite comme tableau à 1
    // élément (rétro-compat des snapshots stockés côté client).
    if (colFilters.type) {
      const arr2 = Array.isArray(colFilters.type) ? colFilters.type : [colFilters.type];
      if (arr2.length > 0) arr = arr.filter((d) => arr2.includes(d.type_id));
    }
    if (colFilters.project) arr = arr.filter((d) => d.project_id === colFilters.project);
    if (colFilters.creator) arr = arr.filter((d) => d.created_by === colFilters.creator);
    // Spec iter213 (2026-02-28) — filtre par dernier modificateur (MODIFS col).
    if (colFilters.modifier) arr = arr.filter((d) => d.last_modified_by === colFilters.modifier);
    if (colFilters.resp)    arr = arr.filter((d) => currentResponsible(d) === colFilters.resp);

    // Spec rev97 (2026-08-02) — filtre multi-select sur la colonne
    // « Pro. étape ». 4 états mutuellement exclusifs par document,
    // sélection multi (union). Un doc en retard est classé « retard »
    // et NON « en_cours » (les 2 sont indépendants dans le menu).
    if (colFilters.proEtat && Array.isArray(colFilters.proEtat) && colFilters.proEtat.length > 0) {
      const sel = new Set(colFilters.proEtat);
      arr = arr.filter((d) => {
        if (d.status === "cloture") return sel.has("cloture");
        // Spec iter218 (2026-02-28) — ignore les étapes supprimées.
        const openSteps = (d.steps || []).filter((s) => !s.closed_at && !s.deleted_at);
        const liveSteps = (d.steps || []).filter((s) => !s.deleted_at);
        if (openSteps.length === 0 && liveSteps.length > 0) return sel.has("termine");
        const hasLate = openSteps.some((s) => s.is_late === true);
        return sel.has(hasLate ? "retard" : "en_cours");
      });
    }

    // Spec rev102 (2026-02) — filtre multi-select sur la colonne Progression.
    //   not_started = 0 étape close sur un total > 0 (workflow existant)
    //   in_progress = 1 <= closed < total
    //   done        = closed === total (tous fermés, docs sans étape ignorés)
    //   out_of_order (rev123, 2026-02) = étapes closes dans le désordre
    //     (une étape non-close AVANT une étape close). Même logique que
    //     /health/out-of-order-steps côté backend. OR-additif avec les
    //     autres options : un doc "in_progress + out_of_order" match si
    //     l'une OU l'autre est cochée.
    if (colFilters.progress && Array.isArray(colFilters.progress) && colFilters.progress.length > 0) {
      const sel = new Set(colFilters.progress);
      arr = arr.filter((d) => {
        const info = stepInfo(d);
        if (!info.total) return false;
        // Détection de l'ordre incohérent : true dès qu'une étape close
        // suit une étape non-close dans le tableau doc.steps.
        // Spec iter218 (2026-02-28) — les tombstones sont ignorés.
        let seenOpen = false;
        let outOfOrder = false;
        for (const s of (d.steps || [])) {
          if (s.deleted_at) continue;
          const closed = !!s.closed_at;
          if (!closed) seenOpen = true;
          else if (seenOpen) { outOfOrder = true; break; }
        }
        if (outOfOrder && sel.has("out_of_order")) return true;
        const deletedCount = (d.steps || []).filter((s) => s.deleted_at).length;
        const liveTotal = info.total - deletedCount;
        if (info.closed === 0) return sel.has("not_started");
        if (info.closed >= liveTotal) return sel.has("done");
        return sel.has("in_progress");
      });
    }

    // Spec rev102 (2026-02) — filtre multi-select sur la colonne Pièces jointes.
    if (colFilters.attachments && Array.isArray(colFilters.attachments) && colFilters.attachments.length > 0) {
      const sel = new Set(colFilters.attachments);
      arr = arr.filter((d) => {
        const n = attachmentsCount(d);
        if (n > 0) return sel.has("with");
        return sel.has("without");
      });
    }

    // Spec 2026-02-XX rev25 (Point 11) — filtres rapides client-side.
    if (scope === "due_today") {
      // Étape active (non-closed) dont la deadline tombe aujourd'hui.
      // Spec iter218 (2026-02-28) — ignore les étapes supprimées.
      const todayYmd = new Date().toISOString().slice(0, 10);
      arr = arr.filter((d) => {
        const steps = d.steps || [];
        return steps.some((s) => !s.closed_at && !s.deleted_at && s.deadline && s.deadline.slice(0, 10) === todayYmd);
      });
    } else if (scope === "no_attachment") {
      arr = arr.filter((d) => attachmentsCount(d) === 0);
    } else if (scope === "to_revalidate") {
      // Spec rev154 (2026-02-07) — filtre "Étapes à revalider" : docs
      // ayant au moins une étape corrigée (corrected_at posé) non encore
      // revalidée (closed_at effacé). Cohérent avec l'indicateur orange
      // de la colonne Progression (stateFor(s) → #F97316).
      // Spec iter218 (2026-02-28) — ignore les étapes supprimées.
      arr = arr.filter((d) => {
        const steps = d.steps || [];
        return steps.some((s) => s.corrected_at && !s.closed_at && !s.deleted_at);
      });
    }
    arr = [...arr];
    const dir = sortDir === "asc" ? 1 : -1;
    const cmp = (va, vb) => {
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb), "fr", { numeric: true }) * dir;
    };
    arr.sort((a, b) => {
      // Spec rev54 (2026-08-02) — sortKey `recent_activity` = max des
      // dates d'événements (`updated_at`, dernière étape closed, dernier
      // step field updated). DESC forcé (plus récent en tête).
      if (sortKey === "recent_activity") {
        return String(docActivityAt(b)).localeCompare(String(docActivityAt(a)));
      }
      switch (sortKey) {
        case "reference": return cmp(a.reference || "", b.reference || "");
        case "title":     return cmp(a.title || "", b.title || "");
        case "type":      return cmp(typesById[a.type_id]?.name || "", typesById[b.type_id]?.name || "");
        case "status":    return cmp(a.status || "", b.status || "");
        case "project":   return cmp(projectsById[a.project_id]?.name || "", projectsById[b.project_id]?.name || "");
        case "progress": {
          const ai = stepInfo(a); const bi = stepInfo(b);
          const ap = ai.total ? ai.closed / ai.total : 0;
          const bp = bi.total ? bi.closed / bi.total : 0;
          return (ap - bp) * dir;
        }
        case "creator": {
          const au = usersById[a.created_by]; const bu = usersById[b.created_by];
          return cmp(au ? `${au.first_name} ${au.last_name}` : "",
                    bu ? `${bu.first_name} ${bu.last_name}` : "");
        }
        case "resp": {
          const au = usersById[currentResponsible(a)]; const bu = usersById[currentResponsible(b)];
          return cmp(au ? `${au.first_name} ${au.last_name}` : "",
                    bu ? `${bu.first_name} ${bu.last_name}` : "");
        }
        case "suivi": {
          // Date la plus pertinente = même règle que DocumentSuiviCell.
          const pick = (d) => d.finalized_at || d.closed_at || d.terminee_at || d.created_at || "";
          return cmp(pick(a), pick(b));
        }
        case "attachments": return (attachmentsCount(a) - attachmentsCount(b)) * dir;
        case "created_at":
        default:            return cmp(a.created_at || "", b.created_at || "");
      }
    });
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, sortKey, sortDir, colFilters, scope, typesById, projectsById, usersById, categoryFilter, types]);

  // Options pour les menus de filtre (déduites de la liste chargée).
  // Spec iter213 (2026-02-28) — Helper : renvoie l'option de dropdown
  // enrichie avec l'avatar de l'utilisateur (initiales + palette
  // déterministe via avatarColorForSeed). Utilisé par les 3 colonnes
  // « personne » : CRÉÉ LE (creator), MODIFS (modifier), RESPO (resp).
  const _userOption = (u) => {
    if (!u) return null;
    const initials = initialsOf(u);
    const pal = avatarColorForSeed(u.id || u.email || `${u.first_name}${u.last_name}`);
    return {
      value: u.id,
      label: `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email || u.id,
      avatar: { initials, bg: pal.bg, fg: pal.fg, user: u },
    };
  };

  const creatorOptions = useMemo(() => {
    const seen = new Set(); const opts = [];
    for (const d of items) {
      const u = usersById[d.created_by];
      if (u && !seen.has(u.id)) {
        seen.add(u.id);
        const opt = _userOption(u);
        if (opt) opts.push(opt);
      }
    }
    return opts.sort((a, b) => a.label.localeCompare(b.label));
  }, [items, usersById]);
  // Spec iter213 (2026-02-28) — options utilisateurs pour la colonne
  // MODIFS (filtre par « dernier modificateur »). Basé sur
  // ``last_modified_by`` stampé par ``_stamp_modifs`` (spec iter210).
  const modifierOptions = useMemo(() => {
    const seen = new Set(); const opts = [];
    for (const d of items) {
      if (!d.last_modified_by) continue;
      const u = usersById[d.last_modified_by];
      if (u && !seen.has(u.id)) {
        seen.add(u.id);
        const opt = _userOption(u);
        if (opt) opts.push(opt);
      }
    }
    return opts.sort((a, b) => a.label.localeCompare(b.label));
  }, [items, usersById]);
  const respOptions = useMemo(() => {
    const seen = new Set(); const opts = [];
    for (const d of items) {
      const uid = currentResponsible(d);
      const u = usersById[uid];
      if (u && !seen.has(u.id)) {
        seen.add(u.id);
        const opt = _userOption(u);
        if (opt) opts.push(opt);
      }
    }
    return opts.sort((a, b) => a.label.localeCompare(b.label));
  }, [items, usersById]);
  const typeOptions = useMemo(
    () => (types || []).map((t) => ({ value: t.id, label: t.name })).sort((a, b) => a.label.localeCompare(b.label)),
    [types]
  );
  const projectOptionsInList = useMemo(() => {
    const seen = new Set(); const opts = [];
    for (const d of items) {
      if (!d.project_id) continue;
      const p = projectsById[d.project_id];
      if (p && !seen.has(p.id)) {
        seen.add(p.id);
        opts.push({ value: p.id, label: p.name });
      }
    }
    return opts.sort((a, b) => a.label.localeCompare(b.label));
  }, [items, projectsById]);
  const statusOptions = Object.entries(DOC_STATUS_MAP).filter(([k]) => k !== "terminee").map(([k, v]) => ({
    value: k, label: v.label, color: STATUS_COLOR[k],
  }));

  // Spec rev139 (2026-02-06) — Compteur par puce de filtre actif : pour
  // chaque puce (Type: Chèque émis, Statut: En cours, …) on calcule le
  // nombre de documents (dans la liste chargée `items`) qui matchent CE
  // filtre SEUL, indépendamment des autres filtres. Cela donne un
  // « facet count » utile pour comparer les filtres entre eux.
  const getChipCount = (k, v) => {
    switch (k) {
      case "status":
        return items.filter((d) => d.status === v).length;
      case "type":
        return items.filter((d) => d.type_id === v).length;
      case "project":
        return items.filter((d) => d.project_id === v).length;
      case "creator":
        return items.filter((d) => d.created_by === v).length;
      case "modifier":
        // Spec iter213 (2026-02-28)
        return items.filter((d) => d.last_modified_by === v).length;
      case "resp":
        return items.filter((d) => currentResponsible(d) === v).length;
      case "proEtat":
        return items.filter((d) => {
          if (d.status === "ouvert") return v === "ouvert";
          if (d.status === "cloture") return v === "cloture";
          // Spec iter218 (2026-02-28) — ignore les étapes supprimées.
          const openSteps = (d.steps || []).filter((s) => !s.closed_at && !s.deleted_at);
          const liveSteps = (d.steps || []).filter((s) => !s.deleted_at);
          if (openSteps.length === 0 && liveSteps.length > 0) return v === "termine";
          const hasLate = openSteps.some((s) => s.is_late === true);
          return v === (hasLate ? "retard" : "en_cours");
        }).length;
      case "progress":
        return items.filter((d) => {
          const info = stepInfo(d);
          if (!info.total) return false;
          let seenOpen = false, outOfOrder = false;
          for (const s of (d.steps || [])) {
            if (s.deleted_at) continue;
            const closed = !!s.closed_at;
            if (!closed) seenOpen = true;
            else if (seenOpen) { outOfOrder = true; break; }
          }
          const deletedCount = (d.steps || []).filter((s) => s.deleted_at).length;
          const liveTotal = info.total - deletedCount;
          if (v === "out_of_order") return outOfOrder;
          if (info.closed === 0) return v === "not_started";
          if (info.closed >= liveTotal) return v === "done";
          return v === "in_progress";
        }).length;
      case "attachments":
        return items.filter((d) => {
          const n = attachmentsCount(d);
          return v === "with" ? n > 0 : n === 0;
        }).length;
      default:
        return 0;
    }
  };

  return (
    <div
      data-testid="documents-page"
      className="flex flex-col gap-4 min-h-0"
      style={{ height: "calc(100vh - 80px)" }}
    >
      {/* --- Header 2 lignes -------------------------------------------- */}
      <DocumentsHeader
        stats={stats}
        activeKey={activeKpi}
        suspendedActive={scope === "suspended"}
        onSelectKpi={onSelectKpi}
        onNavigateTypes={() => navigate("/documents/types")}
        onOpenTrash={() => navigate("/documents/corbeille")}
        onCreate={() => setCreateOpen(true)}
        canCreate={types.length > 0 && canPerm("documents", "create")}
        hideCreate={!canPerm("documents", "create")}
        hasTypes={types.length > 0}
        isAdminLevel={isAdminLevel}
        viewScope={viewScope}
        onViewScopeChange={setViewScope}
        headerProfileSlot={<HeaderProfileGroup />}
      />

      {/* --- Recherche + Statut ----------------------------------------- */}
      {/* Spec iter183 (2026-02-27) — barre de filtres extraite vers
          `components/documents/DocumentFilters.jsx` (Point 1b du
          backlog technique). Aucun changement visuel : passage
          explicite de tous les state/setters/lookups en props.
          Les composants locaux (ActiveColumnFilterChips, QuickChip,
          PeriodePicker, LocationChipDropdown) et les constantes
          (NONE, QUICK_G1, QUICK_G3, DOC_STATUS_MAP) restent définis
          dans ce fichier et sont passés en props. */}
      <DocumentFilters
        NONE={NONE}
        DOC_STATUS_MAP={DOC_STATUS_MAP}
        QUICK_G1={QUICK_G1}
        QUICK_G3={QUICK_G3}
        typeCategories={typeCategories}
        searchInputRef={searchInputRef}
        search={search}
        searching={searching}
        setSearch={setSearch}
        searchFocused={searchFocused}
        setSearchFocused={setSearchFocused}
        debouncedSearch={debouncedSearch}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        categoryFilter={categoryFilter}
        setCategoryFilter={setCategoryFilter}
        scope={scope}
        setScope={setScope}
        groupByCategory={groupByCategory}
        setGroupByCategory={setGroupByCategory}
        colFilters={colFilters}
        setColFilters={setColFilters}
        getChipCount={getChipCount}
        typesById={typesById}
        projectsById={projectsById}
        usersById={usersById}
        period={period}
        setPeriod={setPeriod}
        projectFilter={projectFilter}
        setProjectFilter={setProjectFilter}
        projectStats={projectStats}
        projects={projects.filter((p) => p.in_choices !== false)}
        items={items}
      />

      {/* --- Toggle « Activité récente » + bouton Imprimer.
             Spec iter266 point 5 (2026-02) : la case « Afficher les clôturés »
             a été retirée — redondante avec le KPI « Clôturé » qui filtre
             déjà exclusivement sur ce statut. Le KPI garantit le forçage
             de `showClosed=true` en interne via `onSelectKpi` (voir la
             logique de la carte KPI). */}
      <div className="flex items-center justify-end gap-3 -mt-2 mb-1">
        <div className="flex items-center" data-testid="docs-toggle-group">
          <button
            type="button"
            onClick={() => {
              if (sortKey === "recent_activity") { setSortKey("created_at"); setSortDir("desc"); }
              else                                { setSortKey("recent_activity"); setSortDir("desc"); }
            }}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-l-md transition-colors"
            style={{
              background: sortKey === "recent_activity" ? "rgba(249,188,38,0.14)" : "rgba(255,255,255,0.03)",
              border: `1px solid ${sortKey === "recent_activity" ? "rgba(249,188,38,0.55)" : "rgba(255,255,255,0.1)"}`,
              color: sortKey === "recent_activity" ? "#F9BC26" : "#EDEDED",
              fontWeight: 600,
            }}
            title="Trie les documents par activité récente (dernière étape validée, dernier upload, dernière modification)."
            data-testid="docs-sort-recent-activity"
          >
            <Clock size={12} strokeWidth={2.4} />
            Activité récente
          </button>

          <span aria-hidden="true" style={{ width: 1, alignSelf: "stretch", background: "rgba(255,255,255,0.14)" }} />
          <label className="inline-flex items-center gap-2 text-xs cursor-pointer select-none px-3 py-1.5 rounded-r-md transition-colors"
            style={{ background: showClosed ? "rgba(30,107,62,0.15)" : "rgba(255,255,255,0.03)", border: `1px solid ${showClosed ? "var(--status-cloture-docs)66" : "rgba(255,255,255,0.1)"}`, borderLeft: "none", color: showClosed ? "var(--status-cloture-docs)" : "#EDEDED" }}
            data-testid="docs-toggle-show-closed">
            <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} className="w-3.5 h-3.5 accent-[var(--status-cloture-docs)]" data-testid="docs-toggle-show-closed-input" />
            Afficher les clôturés
          </label>
        </div>
        {/* Bouton d'action « Étapes à corriger · N » : masqué du DOM si N = 0, toggle du filtre scope=to_revalidate, compteur live. */}
        {(() => {
          const n = (items || []).reduce((acc, d) => acc + ((d.steps || []).some((s) => s.corrected_at && !s.closed_at && !s.deleted_at) ? 1 : 0), 0);
          if (!n) return null;
          const active = scope === "to_revalidate";
          return (
            <button type="button" onClick={() => { setScope(active ? "" : "to_revalidate"); setStatusFilter(""); }}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md transition-colors"
              style={{ background: active ? "rgba(217,155,30,.26)" : "rgba(217,155,30,.14)", border: `1px solid ${active ? "#D99B1E" : "rgba(217,155,30,.4)"}`, color: "#e6c383", fontWeight: 500, boxShadow: active ? "0 0 0 3px rgba(217,155,30,.14)" : "none" }}
              title="Documents ayant au moins une étape en état « À corriger »" aria-pressed={active} data-testid="docs-chip-to-revalidate">
              <AlertTriangle size={12} strokeWidth={2.4} />Étapes à corriger · {n}
            </button>
          );
        })()}
        {/*
          Spec 2026-02-XX (Feb 2026) — bouton "Imprimer les résultats (N)".
          Placement à droite du groupe toggles. Accent bleu Documents #2B579A.
          Désactivé quand la liste est vide.
        */}
        {/* Spec iter154 (Feb 2026) — le compteur reflète le VRAI total
            de résultats matchés côté serveur (``totalCount``), pas
            uniquement les documents chargés dans la page courante. Ainsi,
            un filtre serveur type=Chèque retournant 10 000 docs affiche
            « Imprimer les résultats (10 000) » — plus de plafond fantôme.
            Les filtres client-only additionnels (progression, en retard,
            pièces jointes…) peuvent réduire à la marge la liste imprimée
            mais concernent des cas rares ; l'impression relit tous les
            IDs via /documents?per_page=50000 puis applique ces mêmes
            filtres client, exactement comme le tableau. */}
        {(() => {
          const clientOnlyFilterActive =
            !!colFilters.status ||
            !!colFilters.creator ||
            !!colFilters.modifier ||
            !!colFilters.resp ||
            (Array.isArray(colFilters.proEtat) && colFilters.proEtat.length > 0) ||
            (Array.isArray(colFilters.progress) && colFilters.progress.length > 0) ||
            (Array.isArray(colFilters.attachments) && colFilters.attachments.length > 0) ||
            !!categoryFilter;
          // Quand un filtre client-only est actif, on ne peut pas
          // garantir totalCount ≠ sortedItems.length ; on borne alors
          // par le plus petit des deux pour éviter d'annoncer plus que
          // ce qui sera réellement imprimé. Sinon totalCount = vrai
          // total serveur (correct).
          //
          // Spec iter251 (2026-03-01) — les documents annulés sont
          // TOUJOURS exclus du compteur (comme de l'export). On soustrait
          // les annulés visibles dans la page courante ; si le total
          // serveur inclut des annulés hors page, ils seront filtrés
          // côté serveur lors du POST print-summary de toute façon.
          const cancelledInView = sortedItems.filter((d) => d.status === "annule" || d.cancelled).length;
          const rawCount = clientOnlyFilterActive
            ? Math.min(sortedItems.length, totalCount || sortedItems.length)
            : (totalCount || sortedItems.length);
          const visibleCount = Math.max(0, rawCount - cancelledInView);
          const printable = visibleCount > 0 && !loading && canPerm("documents", "print");
          return (
            <button
              type="button"
              onClick={printSummary}
              disabled={!printable}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: printable ? "rgba(43,87,154,0.14)" : "rgba(255,255,255,0.03)",
                border: `1px solid ${printable ? "#2B579A" : "rgba(255,255,255,0.1)"}`,
                color: printable ? "#5A9EE0" : "#6B7280",
                fontWeight: 600,
              }}
              data-testid="docs-print-summary"
              title={printable ? `Imprimer une synthèse PDF des ${visibleCount} document(s) matchant les filtres actifs` : (!canPerm("documents", "export") ? "Droit « Exporter » requis" : "Aucun document à imprimer")}
            >
              <Printer size={13} strokeWidth={2.2} />
              Imprimer les résultats ({visibleCount})
            </button>
          );
        })()}
      </div>

      {/* --- Tableau (groupes de colonnes) ------------------------------ */}
      {/*
        Spec 2026-02-XX rev4 (Feb 2026) — Refonte responsive complète du
        tableau Documents (validation utilisateur sur mockup 3 largeurs) :
          • table-layout:fixed + <colgroup> % → responsive 1200-1920px sans
            aucun scroll horizontal.
          • Réf en monospace bleu (au lieu du pill jaune plein).
          • Type = pill discrète (fond teinté).
          • Statut = pill avec ● + fond teinté (DocumentStatusBadge existant).
          • Row left accent 2px via boxShadow inset (au lieu de la barre
            inline 3px dans la première cellule).
          • Progression = 1 col avec dots réels du workflow + "N/M".
          • Créé / Resp = avatars 28×28 (au lieu de texte nom complet).
          • Suivi = 1 col fusionnée (4 dates → 1 pill colorée + tooltip 4 dates).
          • Actions = 3 boutons pastilles compacts (existants, palette Info→bleu).
      */}
      <div
        className="rounded-md border border-vertec-borderDark w-full flex-1 min-h-0"
        style={{
          overflowY: "auto",
          overflowX: "hidden",
          background: "linear-gradient(180deg, #212121 0%, #1C1C1C 100%)",
        }}
        data-testid="docs-list-scroll"
      >
        <table className="w-full text-sm" style={{ tableLayout: "fixed" }}>
          <colgroup>
            {/* Spec iter164e (2026-02-25) — Rebalancement pour docs à
                nombre d'étapes élevé (10-12+). Analyse :
                • PROG 7% (~134px @ 1920) était TROP étroit — 10 dots
                  adaptés × 11px + 9 gaps × 4px + compteur = 184px.
                • TYPE 20% avait de la marge — badges dépassent
                  rarement 12% de largeur (« DEMO ITER118B —
                  PROPAGATION RESP » = 36 char × ~5px ≈ 180px, tient
                  largement dans 16%).
                → On donne 4 points à PROG en les prenant sur TYPE.
                11 colonnes visibles (12 quand tri « Activité récente »).
                Somme = 100%. Zéro scroll horizontal de 1200 à 1920 px.

                Ordre : Réf 5 · Créé le 10 · Titre 12 · Type 16 · Statut
                9 · Siège 8 · Prog 11 · Respo 7 · Pro. étape 8 · Pièces
                5 · Actions 9 = 100%. Le badge Type reste affiché EN
                ENTIER (whitespace-normal + wordBreak dans le corps).

                Spec iter164g/h (2026-02-25) — Alternance discrète 1 col
                sur 2 : `backgroundColor: rgba(255,255,255,0.04)` sur
                les cols à index impair (Créé le, Type, Siège, Respo,
                📎). Sans trait vertical — juste un léger contraste
                pour aider l'œil à suivre les colonnes. Les cellules
                <td> et <tr> restent transparentes, laissant le col
                painter le fond. L'en-tête <th> applique le tint
                blended sur #262626 (voir plus bas).
            */}
            {/* Spec iter265 point 1 (2026-02) — Colonne Activité rétrécie
                au strict nécessaire (4 %, contenu court « 12 min », « 3 j »,
                « 1 an »). Titre/Type/Prog restent à leurs largeurs originales
                car la colonne Activité récupère assez d'espace en réduisant
                UNIQUEMENT sa propre largeur.
                Sans Activité : 5+6.5+6.5+12+16+9+7+11+5+6+4+9 = 97 %.
                Avec Activité : idem + 4 % = 101 % (marge acceptable). */}
            <col style={{ width: "5%"  }} />{/* Réf */}
            <col style={{ width: "6.5%", backgroundColor: "rgba(255,255,255,0.04)" }} />{/* Créé le */}
            <col style={{ width: "6.5%" }} />{/* MODIFS (iter208) */}
            <col style={{ width: "12%", backgroundColor: "rgba(255,255,255,0.04)" }} />{/* Titre */}
            <col style={{ width: "16%" }} />{/* Type */}
            <col style={{ width: "9%", backgroundColor: "rgba(255,255,255,0.04)"  }} />{/* Statut */}
            <col style={{ width: "7%"  }} />{/* Siège / Projet */}
            <col style={{ width: "11%", backgroundColor: "rgba(255,255,255,0.04)" }} />{/* Prog */}
            <col style={{ width: "5%"  }} />{/* Respo (réduit iter208) */}
            <col style={{ width: "6%", backgroundColor: "rgba(255,255,255,0.04)"  }} />{/* Étape (simplifiée iter208) */}
            <col style={{ width: "4%" }} />{/* PJ (réduit iter208) */}
            {sortKey === "recent_activity" && (
              <col style={{ width: "6%", minWidth: 84, backgroundColor: "rgba(255,255,255,0.04)" }} />  /* Colonne « Activité » : 6 % / min 84 px — « à l'instant » tient sans déborder. */
            )}
            {/* Spec iter208 (2026-02-28) — Actions : tint conditionnel pour
                préserver l'alternance stricte (dark/light) selon la présence
                de la colonne Activity :
                  • Activity OFF → Actions à idx 11 (impair) → tint
                  • Activity ON  → Actions à idx 12 (pair)   → dark
                Aligne exactement le pattern déjà utilisé par les <th>
                (isAltCol = i % 2 === 1). */}
            <col
              style={{
                width: "9%",
                ...(sortKey !== "recent_activity"
                  ? { backgroundColor: "rgba(255,255,255,0.04)" }
                  : {}),
              }}
            />{/* Actions (Info + Modifier + Supprimer) */}
          </colgroup>
          <thead>
            {/* Spec 2026-02-XX rev42 (Point 2) — Ligne d'en-tête de GROUPE
                (Identification / Destination / Personnes / …) supprimée
                pour libérer l'espace vertical au-dessus du tableau. Elle
                n'apportait aucune information opérationnelle. */}
            {/* Ligne 2 — column headers (spec 2026-02-XX rev7 : chaque en-tête
                a un menu ▾ pour tri croissant/décroissant + filtre par
                valeur quand pertinent). */}
            <tr>
              {(() => {
                const HDRS = [
                  { key: "reference",   label: "Réf.",              tip: "Référence du document",                             options: null,                  filterKey: null,      multi: false },
                  // Spec iter148 (Feb 2026) — nouvelle colonne fusionnée
                  // « Date de création » : date + trait vertical + avatar
                  // du créateur (remplace l'ancienne colonne « Créé par »).
                  // Spec iter164 (2026-02-25) — libellé raccourci « Créé le »
                  // pour la nouvelle disposition (voir tâche 2 : réorg
                  // complète du tableau).
                  { key: "created_at",  label: "Créé le",           tip: "Date de création + créateur",                       options: creatorOptions,        filterKey: "creator", multi: false, filterLabel: "Filtrer par utilisateur" },
                  // Spec iter208 (2026-02-28) — Nouvelle colonne MODIFS :
                  // date + avatar du dernier modificateur (max des events
                  // d'édition post-création). Tri = effective_activity_at
                  // (max created_at / last_modified_at) déjà appliqué côté
                  // backend sur les listes.
                  // Spec iter213 (2026-02-28) — Filtre par dernier modificateur
                  // (last_modified_by, stampé par _stamp_modifs / iter210).
                  { key: "last_modified_at", label: "Modifs", tip: "Dernière modification + auteur", options: modifierOptions, filterKey: "modifier", multi: false, filterLabel: "Filtrer par utilisateur" },
                  { key: "title",       label: "Titre",             tip: "Titre du document",                                 options: null,                  filterKey: null,      multi: false },
                  // Spec 2026-02-XX rev10 — multi-sélection sur Type.
                  { key: "type",        label: "Type",              tip: "Type de document",                                  options: typeOptions,           filterKey: "type",    multi: true  },
                  { key: "status",      label: "Statut",            tip: "Statut effectif",                                   options: statusOptions,         filterKey: "status",  multi: false },
                  // Spec 2026-02-XX rev25 (Point 4) — libellé complet forcé (colonne élargie 6→8%).
                  { key: "project",     label: "Siège / Projet",    tip: "Localisation",                                      options: projectOptionsInList,  filterKey: "project", multi: false },
                  { key: "progress",    label: "Prog",              tip: "Étapes closes du workflow",                         options: [
                    { value: "not_started",     label: "Non commencé",  color: "#9CA3AF" },
                    { value: "in_progress",     label: "En cours",      color: "#F59E0B" },
                    { value: "done",            label: "Terminé",       color: "#22C55E" },
                    // Spec rev123 (2026-02) — Détection des étapes closes dans
                    // le désordre (même logique que /health/out-of-order-steps).
                    { value: "out_of_order",    label: "Ordre incohérent", color: "#EF4444" },
                  ], filterKey: "progress", multi: true },
                  // Spec iter164c (2026-02-25) — Responsable et Pro. étape
                  // RESTAURÉES à la demande utilisateur. Seule la colonne
                  // « Modifications » (icône crayon jaune ✎) reste retirée —
                  // son contenu vit désormais dans l'onglet Historique de
                  // la fiche détail du document.
                  { key: "resp",        label: "Respo",             tip: "Utilisateurs avec droit « Voir » sur ce type",      options: respOptions,           filterKey: "resp",    multi: false, filterLabel: "Filtrer par utilisateur" },
                  { key: "deadline",    label: "Étape",        tip: "Prochaine étape à traiter (survolez pour la liste complète)", options: [
                    { value: "ouvert",   label: "Ouvert",    color: "#e6c383" },
                    { value: "en_cours", label: "En cours",  color: "#EDEDED" },
                    { value: "termine",  label: "Terminé",   color: "#22C55E" },
                    { value: "cloture",  label: "Clôturé",   color: "var(--status-cloture-docs)" },
                    { value: "retard",   label: "En retard", color: "#EF4444" },
                  ], filterKey: "proEtat", multi: true },
                  // Spec 2026-02-XX rev25 (Point 10) — icône trombone légèrement agrandie (rendu cellule).
                  { key: "attachments", label: "📎",                tip: "Pièces jointes",                                    options: [
                    { value: "with", label: "Avec pièce jointe", color: "#22C55E" },
                    { value: "without", label: "Sans pièce jointe", color: "#9CA3AF" },
                  ], filterKey: "attachments", multi: true },
                  // Spec rev55 (2026-08-02) — colonne « Activité » DISCRÈTE,
                  // visible uniquement quand le tri Activité récente est actif.
                  ...(sortKey === "recent_activity"
                    ? [{ key: "activity_at", label: "Activité", tip: "Dernière activité utilisateur (création / modification / étape ouverte ou close)", options: null, filterKey: null, multi: false, sortable: false }]
                    : []),
                  { key: null,          label: "",                  tip: "",                                                   options: null,                  filterKey: null,      multi: false },
                ];
                return HDRS.map((h, i) => {
                  // Spec iter164f (2026-02-25) — TOUS les en-têtes de
                  // colonne sont désormais centrés horizontalement,
                  // conformément à la demande utilisateur. Le composant
                  // ColumnHeaderMenu passé en `inline-flex` respecte
                  // désormais le `text-center` de la cellule parent.
                  // Spec iter164g (2026-02-25) — Alternance discrète :
                  // les <th> à index impair reçoivent le blended tint
                  // #2F2F2F (≈ #262626 + rgba(255,255,255,0.04)) pour
                  // matcher le fond des cols correspondantes dans le
                  // <colgroup>. Sans cette override, l'inline
                  // `background: "#262626"` du <th> obscurcirait la
                  // teinte du <col> et casserait la lecture verticale.
                  const isAltCol = i % 2 === 1;
                  return (
                  <th
                    key={i}
                    className="text-center px-3 py-2 select-none truncate"
                    title={h.tip || undefined}
                    style={{
                      // Spec 2026-02-XX rev43 — top:0 après retrait rev42 de la
                      // ligne d'en-tête de groupe. z-index élevé (30) pour rester
                      // au-dessus des avatars empilés (Responsable : zIndex 10-i).
                      position: "sticky", top: 0, zIndex: 30,
                      background: isAltCol ? "#2F2F2F" : "#262626",
                      borderBottom: "1px solid rgba(255,255,255,0.08)",
                      fontSize: 10, letterSpacing: "0.03em", textTransform: "uppercase",
                      color: "#9CA3AF", fontWeight: 700,
                    }}
                  >
                    {h.key ? (
                      <ColumnHeaderMenu
                        label={h.label}
                        options={h.options}
                        current={h.filterKey ? (colFilters[h.filterKey] || (h.multi ? [] : "")) : (h.multi ? [] : "")}
                        onSort={(dir) => doSort(h.key, dir)}
                        onFilter={h.filterKey ? (v) => doFilter(h.filterKey, v) : null}
                        sortActive={sortKey === h.key}
                        sortDir={sortDir}
                        testId={`docs-${h.key}`}
                        accent="#5A9EE0"
                        multi={h.multi}
                        filterLabel={h.filterLabel}
                      />
                    ) : (
                      h.label
                    )}
                  </th>
                  );
                });
              })()}
            </tr>
          </thead>
          <tbody>
            {(loading || !refsReady) && !loadError ? (
              <tr><td colSpan={12} className="text-center py-8 text-muted-foreground" data-testid="docs-loading">
                <span className="inline-flex items-center gap-2"><span className="inline-block w-3.5 h-3.5 rounded-full border-2 border-[#3568B0] border-t-transparent animate-spin" />Chargement…</span>
              </td></tr>
            ) : loadError ? (
              <tr><td colSpan={12} className="text-center py-8" data-testid="docs-load-error">
                <div className="inline-flex flex-col items-center gap-3">
                  <span className="text-sm" style={{ color: "#f87171" }}>{loadError} Réessayez.</span>
                  <button type="button" onClick={async () => { setLoadError(null); if (!refsReady) { setLoading(true); loaderRef.current.main = true; if (!(await loadRefs())) return; } load({ append: false, targetPage: 1 }); }} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md transition-colors hover:bg-[#3568B0]/20" style={{ border: "1px solid #3568B0", color: "#3568B0" }} data-testid="docs-load-retry">Réessayer</button>
                </div>
              </td></tr>
            ) : sortedItems.length === 0 ? (() => {
              // Spec rev160 (2026-02) — état vide contextuel :
              //   • s'il existe un filtre / recherche actif → « Aucun résultat »
              //   • sinon (base réellement vide) → « Aucun document. Cliquez… »
              // Ceci corrige un bug où la recherche d'un terme inexistant
              // affichait à tort « Aucun document » (message trompeur qui
              // laisse croire que la base est vide alors qu'il n'y a
              // simplement aucun résultat pour la recherche saisie).
              const trimmedSearch = search.trim();
              const hasActiveFilter =
                !!trimmedSearch ||
                !!statusFilter ||
                !!categoryFilter ||
                !!scope ||
                !!projectFilter ||
                !!(period?.from || period?.to) ||
                Object.values(colFilters || {}).some((v) =>
                  Array.isArray(v) ? v.length > 0 : !!v,
                );
              // Spec iter130 (Feb 2026) — état vide unifié (EmptyState).
              const clearAllFilters = () => {
                try { sessionStorage.removeItem(RESTORE_KEY); } catch { /* ignore */ }
                clearScrollMemory(RESTORE_KEY, "[data-testid=docs-list-scroll]");
                setSearch("");
                setStatusFilter("");
                setCategoryFilter("");
                setScope("");
                setProjectFilter?.("");
                setPeriod?.({ from: "", to: "" });
                setColFilters({});
              };
              let title, description, ctaLabel = null, onCta = null;
              if (!hasActiveFilter && viewScope === "mine") {
                title = "Aucun document personnel";
                description = "Vous n'êtes créateur, responsable ou intervenant d'aucun document. Basculez sur « Globale » pour voir tous les documents auxquels votre droit « Voir » donne accès.";
                ctaLabel = "Afficher la vue Globale";
                onCta = () => setViewScope("global");
              } else if (!hasActiveFilter) {
                title = "Aucun document";
                description = "Créez votre premier document pour démarrer un workflow (rapport, contrat, PV…).";
                ctaLabel = "+ Créer un document";
                onCta = () => setCreateOpen(true);
              } else if (trimmedSearch) {
                title = `Aucun résultat pour « ${trimmedSearch} »`;
                description = "Essayez d'affiner la recherche ou d'effacer les filtres.";
                ctaLabel = "Réinitialiser les filtres";
                onCta = clearAllFilters;
              } else {
                title = "Aucun résultat pour ces filtres";
                description = "Essayez d'en retirer un ou plusieurs.";
                ctaLabel = "Réinitialiser les filtres";
                onCta = clearAllFilters;
              }
              return (
                <tr>
                  <td colSpan={12} className="p-0">
                    <EmptyState
                      icon={<FileText size={44} strokeWidth={1.5} />}
                      title={title}
                      description={description}
                      ctaLabel={ctaLabel}
                      onCta={onCta}
                      color={"#3568B0"}
                      testId="docs-empty-state"
                    />
                  </td>
                </tr>
              );
            })() : (groupByCategory ? (() => {
              // Spec rev76 (2026-08-02) — vue « Groupée par catégorie » :
              // on interlève des sentinelles `{__group: cat}` entre les
              // documents pour matérialiser les en-têtes de section.
              // Le corps de la map détecte la sentinelle et renvoie une
              // ligne d'en-tête (colspan=12).
              const NONE_KEY = "__none__";
              const groups = new Map();
              for (const d of sortedItems) {
                const t = typesById[d.type_id];
                const key = t?.category_id || NONE_KEY;
                if (!groups.has(key)) groups.set(key, []);
                groups.get(key).push(d);
              }
              const out = [];
              const catList = [...typeCategories]
                .filter((c) => groups.has(c.id))
                .sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr"));
              for (const c of catList) {
                out.push({ __group: c });
                for (const d of groups.get(c.id)) out.push(d);
              }
              if (groups.has(NONE_KEY)) {
                out.push({ __group: null });
                for (const d of groups.get(NONE_KEY)) out.push(d);
              }
              return out;
            })() : sortedItems).map((d) => {
              // Sentinelle « header de groupe » (rev76).
              if (d && d.__group !== undefined) {
                const cat = d.__group;
                const label = cat?.name || "Sans catégorie";
                const color = cat?.color || "#6B7280";
                return (
                  <tr
                    key={`grp-${cat?.id || "none"}`}
                    data-testid={`docs-group-header-${cat?.id || "none"}`}
                    style={{ background: "#141414", borderTop: "1px solid #242424", borderBottom: "1px solid #242424" }}
                  >
                    <td colSpan={12} className="px-3 py-1.5">
                      <div className="inline-flex items-center gap-2">
                        <span className="inline-block w-2 h-2 rounded-full" style={{ background: color }} />
                        <span className="text-[11px] uppercase font-semibold tracking-widest" style={{ color }}>
                          {label}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              }
              // Spec iter182 (2026-02-27) — extraction ligne de tableau
              // vers `components/documents/DocumentTableRow.jsx` (Point 1a
              // du backlog technique). Aucun changement visuel : passage
              // explicite de toutes les closures/lookups/callbacks en props.
              return (
                <DocumentTableRow
                  key={d.id}
                  d={d}
                  typesById={typesById}
                  types={types}
                  projectsById={projectsById}
                  usersById={usersById}
                  usersLoaded={usersLoaded}
                  typeViewersByType={typeViewersByType}
                  typeViewersLoaded={typeViewersLoaded}
                  DocumentDetailsPreview={DocumentDetailsPreview}
                  DocumentProgressCell={DocumentProgressCell}
                  DocumentSuiviCell={DocumentSuiviCell}
                  user={user}
                  isAdminLevel={isAdminLevel}
                  canPerm={canPerm}
                  docScope={docScope}
                  stepInfo={stepInfo}
                  attachmentsCount={attachmentsCount}
                  activeStepsAll={activeStepsAll}
                  activeStep={activeStep}
                  daysLate={daysLate}
                  pickUrgentStep={pickUrgentStep}
                  typeBadgeColor={typeBadgeColor}
                  fmtDateShort={fmtDateShort}
                  fmtDateFull={fmtDateFull}
                  fmtDateTimeShort={fmtDateTimeShort}
                  formatRelativeShort={formatRelativeShort}
                  docActivityAt={docActivityAt}
                  sortKey={sortKey}
                  saveFiltersSnapshot={saveFiltersSnapshot}
                  navigate={navigate}
                  openEdit={openEdit}
                  setDeleteDlg={setDeleteDlg}
                  projects={projects}
                />
              );
            })}
          </tbody>
        </table>
        {/* Sentinel infinite scroll (spec 2026-02-XX rev10) — invisible,
            déclenche le chargement de la page suivante quand il entre dans
            le viewport (rootMargin 200px). Affiche « Chargement… » quand
            active, sinon rien. Un bouton « Charger plus » sert de fallback
            si l'observer n'est pas supporté ou si l'utilisateur clique. */}
        {hasMore && (
          <div
            ref={sentinelRef}
            className="w-full text-center py-4 text-xs text-muted-foreground"
            data-testid="docs-load-more-sentinel"
          >
            {loadingMore ? (
              <span>Chargement…</span>
            ) : (
              <button
                type="button"
                onClick={loadMore}
                className="underline hover:text-white transition-colors"
                data-testid="docs-load-more-btn"
              >
                Charger plus ({items.length} sur {totalCount})
              </button>
            )}
          </div>
        )}
      </div>

      {/* --- Dialogs ---------------------------------------------------- */}
      <DocumentCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        types={types}
        projects={projects.filter((p) => p.in_choices !== false)}
        onCreated={() => load()}
        onProjectCreated={() => loadProjects().catch(() => {})}
      />
      <DocumentEditDialog
        open={!!editDoc}
        onOpenChange={(v) => { if (!v) setEditDoc(null); }}
        doc={editDoc}
        docType={editDoc ? typesById[editDoc.type_id] : null}
        projects={projects.filter((p) => p.in_choices !== false)}
        onSaved={() => { setEditDoc(null); load(); }}
      />
      <DeleteDocumentDialog
        payload={deleteDlg}
        onOpenChange={(v) => { if (!v) setDeleteDlg(null); }}
        onDeleted={() => { setDeleteDlg(null); load(); }}
      />

      {/*
        Spec 2026-02-XX rev6 (Feb 2026) — Modal d'aperçu du PDF "Synthèse".
        Ouvert automatiquement dès que ``pdfPreview`` est set (retour du POST
        /export/print-summary). L'utilisateur choisit de télécharger ou
        d'abandonner. On utilise l'``<iframe>`` sur le blob URL pour tirer
        parti du PDF viewer natif du navigateur (zoom/scroll/impression
        directe). Object URL révoqué à la fermeture pour éviter la fuite.
      */}
      <Dialog open={!!pdfPreview} onOpenChange={(v) => { if (!v) closePdfPreview(); }}>
        <DialogContent
          className="bg-card border-[#3A3A3A] p-0 max-w-[95vw] w-[95vw] flex flex-col gap-0"
          style={{ height: "92vh" }}
          data-testid="pdf-preview-dialog"
        >
          <DialogHeader className="px-4 pt-3 pb-2 border-b border-[#3A3A3A] shrink-0">
            <DialogTitle className="flex items-center justify-between text-sm">
              <span>
                <span className="text-muted-foreground mr-2">Aperçu du PDF —</span>
                <span className="font-semibold">{pdfPreview?.filterLabel || "Synthèse"}</span>
              </span>
              <div className="flex items-center gap-2 mr-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={closePdfPreview}
                  data-testid="pdf-preview-close"
                >
                  Fermer
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={printPdfDirectly}
                  data-testid="pdf-preview-print-direct"
                  title="Envoyer directement à l'imprimante sans sauvegarder"
                >
                  <Printer size={13} className="mr-1.5" />
                  Imprimer directement
                </Button>
                <Button
                  size="sm"
                  onClick={downloadPdfPreview}
                  data-testid="pdf-preview-download"
                  style={{ background: "#2B579A", color: "#fff" }}
                >
                  <FileDown size={13} className="mr-1.5" />
                  Télécharger
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          {pdfPreview?.url && (
            <iframe
              src={pdfPreview.url}
              title="Aperçu synthèse PDF"
              // Spec 2026-02-XX rev7 (bug fix Feb 2026) — le DialogContent est
              // maintenant un flex-col, donc l'iframe pousse à 1 1 auto et
              // occupe TOUTE la hauteur restante. `display: block` évite le
              // ~4px de baseline gap qu'un iframe inline aurait laissé. `w-full`
              // + width:100% par défaut, on force aussi height:100% pour les
              // navigateurs qui ne propagent pas la contrainte flex à un
              // iframe (Chromium a été inconstant sur ce point).
              className="w-full"
              style={{
                flex: "1 1 auto",
                width: "100%",
                height: "100%",
                minHeight: 0,
                border: "none",
                background: "#fff",
                display: "block",
              }}
              data-testid="pdf-preview-iframe"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ---------------------------------------------------------------------------

const groupHeadStyle = () => ({
  position: "sticky", top: 0, zIndex: 3,
  background: "#1E1E1E",
  color: "#2B579A",
  fontSize: 10, letterSpacing: "0.06em", fontWeight: 700,
  borderBottom: "1px solid rgba(255,255,255,0.05)",
});


// ---------------------------------------------------------------------------
// _LegacyUserAvatarUnused — conservé pour référence mais REMPLACÉ par
// le composant partagé `@/components/ui/UserAvatar` (spec iter82, Feb 2026).
// Ce composant local n'est plus utilisé nulle part dans Documents.jsx.
// ---------------------------------------------------------------------------
// eslint-disable-next-line no-unused-vars
function _LegacyUserAvatarUnused({ userId, usersById, currentUserId, subtitle }) {
  const u = userId ? usersById?.[userId] : null;
  if (!u) return <span className="text-muted-foreground text-xs">—</span>;
  const isMe = userId === currentUserId;
  const initials = initialsOf(u);
  const fullName = `${u.first_name || ""} ${u.last_name || ""}`.trim();
  // Spec 2026-02-XX rev7 — fond dérivé de l'utilisateur (déterministe et
  // partagé avec Tâches / KPI drill-down / TopBar / LockScreen).
  const pal = avatarColorForSeed(u.id || u.email);
  return (
    <AvatarNamePopover
      name={isMe ? `${fullName} (moi)` : fullName}
      subtitle={subtitle}
      testid={`doc-avatar-popover-${userId}`}
    >
      <span
        className="inline-flex items-center justify-center rounded-full font-bold shrink-0"
        style={{
          width: 28, height: 28,
          background: pal.bg,
          color: pal.fg,
          fontSize: 11,
          border: "1px solid rgba(255,255,255,0.15)",
        }}
        title={isMe ? `${fullName} (moi)` : fullName}
      >
        {initials}
      </span>
    </AvatarNamePopover>
  );
}


// ---------------------------------------------------------------------------
// DocumentProgressCell — spec 2026-02-XX rev17 (correction point 7)
//   Un point (13×13, border-radius 3px) par étape RÉELLE du workflow.
//   Système à 6 ÉTATS (rev152 · 2026-02-07 — ajout de l'état orange
//   « corrigée en attente de revalidation ») :
//     • gris     #3A3A3A → étape pas encore faite (future OU en cours vide)
//     • BLEU     #5A9EE0 → étape en cours (contenu déposé, pas encore validée)
//     • vert     #22C55E → étape validée (closed_at)
//     • rouge    #EF4444 → étape en délai dépassé (open + is_late)
//     • ORANGE   #F97316 → étape rouverte via « Corriger » et pas encore
//                          revalidée (corrected_at posé, closed_at effacé).
//                          Halo orange léger, persistant jusqu'à revalidation.
//     • jaune    #EAB308 → comportement GLOBAL : quand le document entier
//                          passe au statut Clôturé, TOUS les points de
//                          toutes les étapes passent au jaune simultanément
//                          peu importe leur état individuel précédent.
//   Tooltip natif au survol (via <title>) — nom de l'étape + statut.
//
//   Le bleu « En cours » a été introduit après le fix rev58 pour rendre
//   lisible les documents historiques qui portaient du contenu sur des
//   étapes non validées (contournement backend corrigé désormais).
// ---------------------------------------------------------------------------
function DocumentProgressCell({ doc, info, statusColor, testId }) {
  const total = info?.total || 0;
  // Spec iter152 P3 — mode Dossier : un point ambre par pièce (max 5 + « +N ») suivi du total.
  if ((doc?.workflow_mode || "") === "dossier") {
    const n = (doc.pieces || []).filter((p) => !p.deleted_at).length;
    if (!n) return <div className="inline-flex items-center" data-testid={testId} data-pieces="0"><span style={{ color: "#5e5e69", fontSize: 11 }}>—</span></div>;
    return (
      <div className="inline-flex items-center" style={{ gap: 6 }} title={`${n} pièce${n > 1 ? "s" : ""} au dossier`} data-testid={testId} data-pieces={n}>
        {/* iter172 — carrés identiques aux étapes (14 px, radius 3), couleur ambre dossier */}
        {Array.from({ length: Math.min(n, 5) }).map((_, i) => <span key={i} style={{ width: 13, height: 13, borderRadius: 3, background: "#E0A82E", display: "inline-block" }} data-testid={`${testId}-square-${i}`} />)}
        {n > 5 && <span style={{ color: "#71717c", fontSize: 9 }}>+{n - 5}</span>}
        <span style={{ color: "#e6c383", fontSize: 11, fontWeight: 600, marginLeft: 2 }}>{n}</span>
      </div>
    );
  }
  // Spec rev106 (2026-02) — Document « sans étape » (workflow_mode ===
  // "simple"). On remplace l'affichage habituel par une barre pleine à
  // hachures diagonales vert clair / vert foncé, tooltip natif « Sans
  // étape ». Volontairement pas d'icône ni de compteur — c'est le
  // signal visuel de l'absence de workflow.
  if ((doc?.workflow_mode || "") === "simple") {
    // Spec rev112 (2026-02) — final visual : une case verte 14×14
    // (identique aux cases de progression normales) + espace 7px +
    // texte « Sans étape » en vert clair. Point de départ (la case)
    // aligné pixel-perfect avec la 1ʳᵉ case des documents "avec étapes".
    return (
      <div className="inline-flex items-center gap-[7px]" data-testid={testId}>
        <span
          title="Sans étape"
          aria-label="Sans étape"
          style={{
            width: 14,
            height: 14,
            borderRadius: 3,
            background: "#22C55E",
            display: "inline-block",
            flexShrink: 0,
          }}
        />
        <span style={{ fontSize: 11, color: "#9CA3AF", lineHeight: 1 }}>
          Sans étape
        </span>
      </div>
    );
  }
  if (total === 0) {
    return <span className="text-[11px] text-muted-foreground italic" data-testid={testId}>—</span>;
  }
  const closed = info?.closed || 0;
  const steps = doc?.steps || [];
  const isDocClotured = doc?.status === "cloture";

  /**
   * hasContent — détecte si une étape porte du contenu (fichier(s)
   * uploadé(s), valeur(s) de champ) même si elle n'est pas encore
   * validée. Sert à décider si le dot passe en bleu « En cours ».
   */
  const hasContent = (s) => {
    if (!s) return false;
    const fv = s.field_values || {};
    for (const k of Object.keys(fv)) {
      const v = fv[k];
      if (v == null) continue;
      if (Array.isArray(v)) { if (v.length > 0) return true; }
      else if (typeof v === "string") { if (v.trim()) return true; }
      else if (v) return true;
    }
    // Legacy fields (avant migration multi-fichiers) — même sémantique.
    if (s.field_value || s.field_filename) return true;
    return false;
  };

  // Spec iter242 (2026-08-31) — Doc ANNULÉ : GARDE les couleurs
  // d'origine des cases (plus de désaturation) et ajoute un TRAIT
  // DIAGONAL sur chaque case + strike-through sur le compteur X/Y.
  // Objectif : indiquer clairement l'arrêt du workflow sans perdre
  // l'information de progression au moment de l'annulation.
  const isDocCancelled = doc?.status === "annule" || !!doc?.cancelled;
  const stateFor = (s) => {
    // Spec iter220 (2026-02-28) — mécanisme tombstone renversé.
    // Une étape ne peut plus être « supprimée » à l'affichage ;
    // « Supprimer » réinitialise l'étape à son état vide, elle
    // se rend donc comme n'importe quelle étape non-atteinte.
    // Chantier 2 (iter195) — priorité : à corriger (orange) > retard (rouge) > validé (vert) > gris.
    if (s?.step_status === "a_corriger") return { color: "#E0822E", label: "À corriger" };
    if (isDocClotured)          return { color: "#22C55E", label: "Doc clôturé" };
    if (s?.closed_at)           return { color: "#22C55E", label: "Validée" };
    if (s?.corrected_at)        return { color: "#E0822E", label: "À corriger" };
    if (s?.is_late)             return { color: "#EF4444", label: "Délai dépassé" };
    if (hasContent(s))          return { color: "#3A3A3A", label: "En cours (contenu déposé, pas encore validée)" };
    return                             { color: "#3A3A3A", label: "Non atteinte" };
  };

  // Tooltip global sur le container, plus lisible que 13 tooltips indiv.
  const tooltip = steps.length
    ? steps
        .map((s, i) => {
          const st = stateFor(s);
          return `${String(i + 1).padStart(2, "0")}. ${s.name || s.label || "Étape"} — ${st.label}`;
        })
        .join("\n")
    : `${closed}/${total} étape(s) closes`;

  // Spec iter164d (2026-02-25) — Rendu adaptatif pour docs à MANY steps.
  // Sans adaptation, 12 dots × 13px + 11 gaps × 6px + text ≈ 256px, ce qui
  // déborde de la colonne 7%. On rétrécit progressivement dots et gap au
  // delà de 7 étapes pour tout garder dans la cellule sans troncature du
  // compteur.
  //   total ≤ 7   : dot 13 · gap 6
  //   total 8-10  : dot 11 · gap 4
  //   total 11-14 : dot  9 · gap 3
  //   total 15+   : dot  7 · gap 2
  const [dotSize, dotGapPx] = total <= 7
    ? [13, 6]
    : total <= 10
    ? [11, 4]
    : total <= 14
    ? [9, 3]
    : [7, 2];

  return (
    <div className="inline-flex items-center gap-2 whitespace-nowrap" data-testid={testId}>
      <span className="inline-flex" style={{ gap: dotGapPx }} title={tooltip}>
        {Array.from({ length: total }).map((_, i) => {
          const s = steps[i];
          const st = s ? stateFor(s) : { color: "#3A3A3A", label: "Non atteinte" };
          const itemTip = s
            ? `${String(i + 1).padStart(2, "0")}. ${s.name || s.label || "Étape"} — ${st.label}`
            : `Étape ${i + 1} — ${st.label}`;
          // Spec iter246 — sur une case 13×13 px le trait diagonal
          // dominait la couleur (« crayon barré »). Fix : trait TRÈS
          // fin (48-52 % = ~0.5 px), edges doucis (46-48 % / 52-54 %),
          // opacité 0.55 → la couleur d'origine (vert vif, rouge, gris)
          // reste immédiatement lisible ; le trait n'est qu'un signal
          // d'annulation superposé, il ne remplace pas la case.
          const cancelSlash = isDocCancelled
            ? `linear-gradient(135deg, transparent 46%, rgba(255,255,255,0.55) 48%, rgba(255,255,255,0.55) 52%, transparent 54%), `
            : "";
          return (
            <span
              key={i}
              title={itemTip}
              style={{
                width: dotSize, height: dotSize, borderRadius: 3,
                background: `${cancelSlash}${st.color}`,
                display: "inline-block",
                overflow: "hidden",
                border: st.color === "#3A3A3A" ? "1px solid rgba(255,255,255,0.05)" : "none",
                boxShadow: st.color === "#F97316"
                  ? "0 0 0 1px rgba(249,115,22,0.35), 0 0 6px rgba(249,115,22,0.55)"
                  : undefined,
              }}
            />
          );
        })}
      </span>
      <span
        className="tabular-nums text-[11px] shrink-0"
        style={{
          color: isDocCancelled ? "#D1D5DB" : "#9CA3AF",
          fontWeight: 600,
          textDecoration: isDocCancelled ? "line-through" : "none",
          textDecorationThickness: isDocCancelled ? "1.5px" : undefined,
        }}
      >
        {closed}/{total}
      </span>
    </div>
  );
}


// ---------------------------------------------------------------------------
// DocumentSuiviCell — spec 2026-02-XX rev4 (Feb 2026)
//   Fusionne les 4 colonnes de dates (Créé le / Terminé le / À vérifier le
//   / Clôturé le) en 1 seule cellule affichant la date la PLUS pertinente
//   selon l'état du document, en pill colorée :
//     • Clôturé (finalized_at) → vert
//     • À vérifier (closed_at) → violet
//     • Terminé (terminee_at)  → bleu
//     • sinon                  → gris "Créé · ..."
//   Tooltip au survol : détail complet des 4 dates (même si vides "—").
// ---------------------------------------------------------------------------
function DocumentSuiviCell({ doc, testId }) {
  const shortDate = (iso) => {
    if (!iso) return "—";
    try {
      const dt = new Date(iso);
      return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}`;
    } catch { return "—"; }
  };
  const longDate = (iso) => {
    if (!iso) return "—";
    try {
      const dt = new Date(iso);
      return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}/${dt.getFullYear()} ${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`;
    } catch { return "—"; }
  };
  // Sélection de la date pertinente selon l'ordre de priorité :
  // finalized > closed > terminee > created.
  let label, dateStr, bg, color;
  if (doc.finalized_at) {
    label = "Clôturé";
    dateStr = shortDate(doc.finalized_at);
    bg = "rgba(34,197,94,0.14)"; color = "#22C55E";
  } else if (doc.closed_at) {
    label = "À vérifier";
    dateStr = shortDate(doc.closed_at);
    bg = "rgba(139,92,246,0.14)"; color = "#A78BFA";
  } else if (doc.terminee_at) {
    label = "Terminé";
    dateStr = shortDate(doc.terminee_at);
    bg = "rgba(59,156,224,0.14)"; color = "#3B9CE0";
  } else {
    label = "Créé";
    dateStr = shortDate(doc.created_at);
    bg = "rgba(255,255,255,0.04)"; color = "#9CA3AF";
  }
  const tooltip = [
    `Créé le : ${longDate(doc.created_at)}`,
    `Terminé le : ${longDate(doc.terminee_at)}`,
    `À vérifier le : ${longDate(doc.closed_at)}`,
    `Clôturé le : ${longDate(doc.finalized_at)}`,
  ].join("\n");
  return (
    <span
      className="inline-flex items-center gap-1 font-semibold rounded truncate max-w-full"
      style={{
        fontSize: 11, padding: "3px 8px", background: bg, color, cursor: "help",
      }}
      data-testid={testId}
      title={tooltip}
    >
      {label} · {dateStr}
    </span>
  );
}


// ---------------------------------------------------------------------------
// ProgressDots — spec 2026-07-17 septies :
// Points reliés — vert validé, or en cours avec halo, gris à venir — +
// pourcentage à côté. Pas de fraction x/y.
// ---------------------------------------------------------------------------
function ProgressDots({ closed = 0, total = 0, percent = 0, testId }) {
  if (total === 0) {
    return <span className="text-[11px] text-muted-foreground italic" data-testid={testId}>—</span>;
  }
  const dots = [];
  for (let i = 0; i < total; i++) {
    const state = i < closed ? "done" : i === closed ? "current" : "todo";
    const color = state === "done" ? "#22C55E" : state === "current" ? "#F9BC26" : "#3A3A3A";
    dots.push(
      <span key={i} className="inline-flex items-center">
        <span className="rounded-full" style={{
          width: 8, height: 8, background: color,
          boxShadow: state === "current" ? "0 0 0 3px rgba(249,188,38,0.28)" : "none",
        }} />
        {i < total - 1 && (
          <span aria-hidden="true" style={{
            display: "inline-block", width: 10, height: 2, marginLeft: 2, marginRight: 2,
            background: i < closed ? "#22C55E55" : "#2A2A2A",
          }} />
        )}
      </span>
    );
  }
  return (
    <div className="inline-flex items-center gap-2" data-testid={testId}>
      <span className="inline-flex items-center">{dots}</span>
      <span className="tabular-nums text-[11px]" style={{
        color: percent === 100 ? "#22C55E" : percent >= 50 ? "#F9BC26" : "#EDEDED",
        fontWeight: 600,
      }}>{percent}%</span>
    </div>
  );
}


// ---------------------------------------------------------------------------
// QuickChip — chip filtre rapide générique (fond neutre / accent quand actif).
// Style aligné sur Tasks (spec 2026-07-17 decies point 5) — flex-1 pour
// largeurs homogènes dans chaque groupe, icône colorée + libellé.
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// DeleteDocumentDialog — motivated soft-delete (Phase B2).
//
// Shows a warning strip explaining the document goes to the archive (not
// permanently lost), a mandatory reason field, and confirms via
// `DELETE /documents/{id}` with body { reason }.
// ---------------------------------------------------------------------------
function DeleteDocumentDialog({ payload, onOpenChange, onDeleted }) {
  const open = !!payload;
  const [saving, setSaving] = useState(false);
  const doc = payload?.doc;

  if (!open) return null;

  const submit = async (reason) => {
    setSaving(true);
    try {
      await api.delete(`/documents/${doc.id}`, { data: { reason } });
      toast.deleted("document", doc.reference, { onUndo: async () => { await api.post(`/documents/${doc.id}/restore`); window.dispatchEvent(new Event("ledesk:doc-mutated")); } });
      onDeleted?.();
    } catch (err) {
      toast.error("Impossible", { description: err.response?.data?.detail || "" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <DeskConfirm open={open} title={<>Supprimer <span className="font-mono" style={{ color: "#5BA3F5" }}>{doc.reference}</span> ?</>}
      message="Le document ne sera pas perdu : il part dans Archives → Supprimés avec votre motif, et un administrateur pourra le restaurer."
      withReason reasonPlaceholder="Motif de la suppression (obligatoire)" busy={saving} confirmLabel={saving ? "Archivage…" : "Archiver"}
      onClose={() => { if (!saving) onOpenChange(false); }} onConfirm={submit} testId="doc-delete-dialog" />
  );
}


// ---------------------------------------------------------------------------
// PeriodePicker — dropdown chip for the "Créé le" date range filter.
//
// UX: one chip button showing either "📅 Période…" or the active range's
// human label ("Aujourd'hui" / "Cette semaine" / "Ce mois" / "du 01/03 au
// 15/03"). Clicking opens a compact popover with:
//   • three shortcuts (Aujourd'hui / Cette semaine / Ce mois)
//   • a custom range picker (from + to native date inputs)
//   • an "Effacer" link when a range is active.
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// LocationChipDropdown — chip that opens a popover listing every project
// with its document count, plus a "Sans siège / projet" entry above the list.
// ---------------------------------------------------------------------------

